import { Injectable } from "@angular/core";
import { ActivatedRoute, NavigationEnd, Params, Router } from "@angular/router";
import { ApolloError, ApolloQueryResult } from '@apollo/client/core';
import { PlusApollo, dayjs } from '@karve.it/core';
import { ComponentStore } from "@ngrx/component-store";
import { tapResponse } from "@ngrx/operators";
import { ChartOptions, SankeyControllerDatasetOptions, SankeyDataPoint } from "chart.js";
import { getHoverColor } from "chart.js/helpers";
import { gql } from "graphql-tag";
import { cloneDeep, isNil, mergeWith, parseInt, startCase, uniq } from "lodash";
import pluralize from "pluralize";
import { MenuItem } from "primeng/api";
import { Observable, Subject, debounceTime, filter, map, of, skip, startWith, switchMap, tap, withLatestFrom } from "rxjs";

import { environment } from '../../environments/environment';
import { baseEnvironment } from '../../environments/environment.base';
import {
  AggregationDataOnlyGQL,
  AggregationDataOnlyQuery,
  BaseConfigValueFragment,
  DataOnlyGQL,
  DataOnlyQuery,
  PreviewReportFilter,
  PreviewReportQueryVariables,
  PreviewReportWithConfigsGQL,
} from "../../generated/graphql.generated";
import { ExtractObservableType } from "../../typescript";
import { ColorSchemeService } from "../color-scheme.service";
import { GraphQLModule } from "../core/public-api";
import { transactionVolumeChartOptions } from "../dashboard/dashboard.constants";
import { PeriodService } from "../dashboard/period.service";
import { ReportParamsForm } from "../dynamic-reports/preview-dynamic-report/preview-dynamic-report.component";

import { DistanceUnit, jobStageIcons, jurisdictions } from "../global.constants";
import { strToTitleCase } from "../js";
import { formatCurrency } from "../lib.ts/currency.util";
import { AdvancedPeriodOptions } from "../reports/reports.constants";
import { BrandingService } from "../services/branding.service";
import { AnyParams, QueryParamsService } from "../shared/query-params.service";
import { currentTimeSeconds, getBeginningOfTimeUnix, getEndOfTimeUnix } from "../time";
import { getParsedConfigValueByKey } from "../utilities/configs.util";
import { getApolloErrorMessage } from "../utilities/errors.util";

import { AmountDelta, CurrencyDelta } from './delta/delta.component';

export const DASHBOARD_PATHS = [
  'revenue',
  'engine',
] as const;

type KeyOfEndingWith<T, Suffix extends string> = {
  [K in keyof T]: K extends `${string}${Suffix}` ? K : never;
}[keyof T];

type SizePrefix = {
  [K in keyof DashboardState]: K extends `${infer S}Count` ?
    `${S}Total` extends keyof DashboardState ? S : never : never;
}[keyof DashboardState];

export type DashboardPath = typeof DASHBOARD_PATHS[number];

export const comparedToLsKey = 'compared-to' as const;

export const comparedToValues = [
  'Previous Month',
  'Same Month Last Year',
] as const;

export const pipelineMetrics = [
  'Job Count',
  'Revenue',
] as const;

export const pipelinePeriods = [
  'Lead Created',
  'Estimated',
  'Booked',
  'Happening',
  'Invoiced',
  'Completed',
] as const;

export type PipelinePeriod = typeof pipelinePeriods[number];

export type PipelineMetric = typeof pipelineMetrics[number];

export interface User {
  name: string;
  id: string;
}

export interface FlowInfo extends SankeyDataPoint {
  selectedJobType: SelectedJobType;
}

export const groupLeadsByValues = [
  'Source',
  'Job Origin',
  'Estimate Method',
  'Estimator',
  'Sales Agent',
  'UTM Source',
  'UTM Medium',
  'UTM Campaign',
  'UTM Content',
  'UTM Term',
] as const;

export type ComparedTo = typeof comparedToValues[number];

export type GroupLeadsBy = typeof groupLeadsByValues[number];

export const sortByValues = [
  'Recently Active',
  'Created On',
  'Customer',
  'Estimator',
  'Job Stage',
] as const;

export type SortBy = typeof sortByValues[number];

const sortByMap: Record<SortBy, string> = {
  'Recently Active': 'jobUpdatedAt DESC',
  'Created On': 'jobCreatedAt DESC',
  'Estimator': 'estimator DESC',
  'Customer': 'customer DESC',
  'Job Stage': 'jobStage DESC',
}

interface ReportData {
  headers: string[];
  rows: string[][];
}
export const jobStages = [ 'Lead', 'Estimate', 'Booking', 'Invoice' ] as const;

export const selectedJobsColumns = [
  'job',
  'customer',
  'jobStage',
  'jobStatus',
  'estimator',
  'discountedSubTotal',
  'jobCreatedAt'
] as const;

export type SelectedJob = {
  code: string;
  id: string;
  description: string;
  createdAt: string;
  discountedSubTotal: string;
  icon: string;
  customer: {
    name: string;
    id: string;
  },
  estimator?: {
    name: string;
    id: string;
  },
  status: 'Open' | 'Closed',
}

export type JobStage = typeof jobStages[number];

type NullableToOptional<T> = T extends infer U | null ? U | undefined : T;

const selectedJobTypeKeys: (keyof DashboardState)[] = [
  'jobStage',
  'status',
  'source',
  'jobOrigin',
  'estimateMethod',
  'jobClosureReason',
  'attentionExplanation',
  'estimator',
  'salesAgent',
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmContent',
  'utmTerm',
] as const;

export type SelectedJobType = {
  [K in keyof Pick<DashboardState, typeof selectedJobTypeKeys[number]>]?: NullableToOptional<DashboardState[K]>
};

// Top to bottom
const stagePriority: Record<string, number> = {
  Lead: 1,
  Estimate: 1,
  Booking: 1,
  Invoice: 1,
};

// Left to right
const stageColumn: Record<string, number> = {
  Lead: 1,
  Estimate: 2,
  Booking: 3,
  Invoice: 4,
};

type ExpandedFlow = Pick<SelectedJobType, 'jobStage' | 'status'>;

const delimiter = '__';

export interface DashboardState {
  period: AdvancedPeriodOptions | 'Month of Year';
  customPeriod: Date[];
  comparedTo: ComparedTo;
  month: string;
  currency: 'USD' | 'CAD' | 'All';
  groupLeadsBy: GroupLeadsBy;
  sortEngineJobsBy: SortBy,
  expandedFlows: ExpandedFlow[];
  pipelinePeriod: PipelinePeriod;
  pipelineMetric: PipelineMetric;
  jobStage: JobStage | null;
  status: 'open' | 'closed' | null;
  source: string | null;
  jobOrigin: string | null;
  estimateMethod: string | null;
  estimator: string | null;
  salesAgent: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string  | null;
  utmContent: string | null;
  utmTerm: string | null;
  attentionExplanation: string | null;
  jobClosureReason: string | null;
  invoiceSizeCount: number;
  invoiceSizeTotal: number;
  averageInvoiceSizeLoading: boolean;
  averageInvoiceSizeError: string | null;
  pastInvoiceSizeCount: number;
  pastInvoiceSizeTotal: number;
  pastAverageInvoiceSizeLoading: boolean;
  jobSizeCount: number;
  jobSizeTotal: number;
  averageJobSizeLoading: boolean;
  averageJobSizeError: string | null;
  pastJobSizeCount: number;
  pastJobSizeTotal: number;
  pastAverageJobSizeLoading: boolean;
  bookingSizeCount: number
  bookingSizeTotal: number;
  averageBookingSizeLoading: boolean;
  averageBookingSizeError: string | null;
  pastBookingSizeCount: number;
  pastBookingSizeTotal: number;
  pastAverageBookingSizeLoading: boolean;
  scheduledJobSizeCount: number;
  scheduledJobSizeTotal: number;
  averageScheduledJobSizeLoading: boolean;
  averageScheduledJobSizeError: string | null;
  pastScheduledJobSizeCount: number;
  pastScheduledJobSizeTotal: number;
  pastAverageScheduledJobSizeLoading: boolean;
  pastAverageScheduledJobSizeError: string | null;
  realizedRevenue: CurrencyAmount;
  scheduledRevenue: CurrencyAmount;
  expectedRevenue: CurrencyAmount;
  sharedRevenue: CurrencyAmount;
  pastRealizedRevenue: CurrencyAmount;
  pastScheduledRevenue: CurrencyAmount;
  pastExpectedRevenue: CurrencyAmount;
  pastSharedRevenue: CurrencyAmount;
  distanceBooked: number;
  distanceCompleted: number;
  travelTimeBooked: number;
  travelTimeCompleted: number;
  pastDistanceBooked: number;
  pastDistanceCompleted: number;
  pastTravelTimeBooked: number;
  pastTravelTimeCompleted: number;
  revenueAndLogisticsLoading: boolean;
  revenueAndLogisticsError: string | null;
  pastRevenueAndLogisticsLoading: boolean;
  transactionsPaidPerDay: string[][];
  eventsCreatedPerDay: string[][];
  eventsHappeningPerDay: string[][];
  activityLoading: boolean;
  activityError: string | null;
  eventsCreated: ReportData;
  eventsCreatedLoading: boolean;
  eventsCreatedError: string | null;
  eventsHappening: ReportData;
  eventsHappeningLoading: boolean;
  eventsHappeningError: string | null;
  sources: string[][];
  sourcesLoading: boolean;
  sourcesError: string | null;
  pipeline: string[][];
  pipelineLoading: boolean;
  pipelineError: string | null;
  engineJobsLoading: boolean;
  engineJobsError: string | null;
  engineJobsHasMore: boolean;
  engineJobsFetchingMore: boolean;
  engineJobs: { [ query: string ]: { data: { rows: string[][] } } },
  units: DistanceUnit;
  lastReload: number;
  jobClosureReasons: { [ closureReasonId: string ]: string };
  attentionExplanations: { [ closureReasonId: string ]: { label: string; description: string } };
}

export interface CurrencyAmount {
  currency: string;
  amount: number;
}

type DashboardReport =
| 'averageInvoiceSize'
| 'averageJobSize'
| 'averageBookingSize'
| 'averageScheduledJobSize'
|  'sources'
|  'eventsCreated'
|  'eventsHappening'
|  'revenueAndLogistics'
|  'leadConversions'
|  'oseConversion'
|  'veConversion'
|  'estimateConversion'
|  'transactionsPaid'
|  'eventsCreatedPerDay'
|  'eventsHappeningPerDay'
|  'pipeline'
|  'engineJobs'
;

const placeholderEventHeaders = [
  'Event Type',
  'Net Subtotal',
  'Event Count',
  'Average Net Subtotal per Event',
];

const placeholderSources = [
  [ 'Not Specified', '0', '0', 'USD' ],
];

export const ENGINE_JOBS_QUERY_LIMIT = 5;

const initialState: DashboardState = {
  period: 'Month of Year',
  customPeriod: getCurrentMonthPeriod(),
  comparedTo: 'Same Month Last Year',
  month: getCurrentMonth(),
  currency: 'All',
  groupLeadsBy: 'Source',
  sortEngineJobsBy: 'Recently Active',
  expandedFlows: [],
  pipelinePeriod: 'Lead Created',
  pipelineMetric: 'Revenue',
  jobStage: 'Lead',
  status: null,
  source: null,
  jobOrigin: null,
  estimateMethod: null,
  estimator: null,
  salesAgent: null,
  utmSource: null,
  utmMedium: null,
  utmCampaign: null,
  utmContent: null,
  utmTerm: null,
  attentionExplanation: null,
  jobClosureReason: null,
  invoiceSizeCount: 0,
  invoiceSizeTotal: 0,
  averageInvoiceSizeLoading: false,
  averageInvoiceSizeError: null,
  pastInvoiceSizeCount: 0,
  pastInvoiceSizeTotal: 0,
  pastAverageInvoiceSizeLoading: false,
  jobSizeCount: 0,
  jobSizeTotal: 0,
  averageJobSizeLoading: false,
  averageJobSizeError: null,
  pastJobSizeCount: 0,
  pastJobSizeTotal: 0,
  pastAverageJobSizeLoading: false,
  bookingSizeCount: 0,
  bookingSizeTotal: 0,
  averageBookingSizeLoading: false,
  averageBookingSizeError: null,
  pastBookingSizeCount: 0,
  pastBookingSizeTotal: 0,
  pastAverageBookingSizeLoading: false,
  scheduledJobSizeCount: 0,
  scheduledJobSizeTotal: 0,
  averageScheduledJobSizeLoading: false,
  averageScheduledJobSizeError: null,
  pastScheduledJobSizeTotal: 0,
  pastScheduledJobSizeCount: 0,
  pastAverageScheduledJobSizeLoading: false,
  pastAverageScheduledJobSizeError: null,
  realizedRevenue: getDefaultAmount(),
  scheduledRevenue: getDefaultAmount(),
  expectedRevenue: getDefaultAmount(),
  sharedRevenue: getDefaultAmount(),
  pastRealizedRevenue: getDefaultAmount(),
  pastScheduledRevenue: getDefaultAmount(),
  pastExpectedRevenue: getDefaultAmount(),
  pastSharedRevenue: getDefaultAmount(),
  revenueAndLogisticsLoading: false,
  revenueAndLogisticsError: null,
  pastRevenueAndLogisticsLoading: false,
  transactionsPaidPerDay: [],
  eventsCreatedPerDay: [],
  eventsHappeningPerDay: [],
  activityLoading: false,
  activityError: null,
  distanceBooked: 0,
  distanceCompleted: 0,
  travelTimeBooked: 0,
  travelTimeCompleted: 0,
  pastDistanceBooked: 0,
  pastDistanceCompleted: 0,
  pastTravelTimeBooked: 0,
  pastTravelTimeCompleted: 0,
  eventsCreated: {
    headers: placeholderEventHeaders,
    rows: [],
  },
  eventsCreatedLoading: false,
  eventsCreatedError: null,
  eventsHappening: {
    headers: placeholderEventHeaders,
    rows: [],
  },
  eventsHappeningLoading: false,
  eventsHappeningError: null,
  sources: placeholderSources,
  sourcesLoading: false,
  sourcesError: null,
  pipeline: [],
  pipelineLoading: false,
  pipelineError: null,
  engineJobsLoading: false,
  engineJobsError: null,
  engineJobsHasMore: false,
  engineJobsFetchingMore: false,
  engineJobs: {},
  units: environment.defaultUnits,
  lastReload: currentTimeSeconds(),
  jobClosureReasons: {},
  attentionExplanations: {},
};

function getDefaultAmount() {
  return {
    currency: baseEnvironment.defaultCurrency,
    amount: 0,
  };
}

@Injectable()
export class DashboardStore extends ComponentStore<DashboardState> {

  constructor(
    private dataOnlyGQL: DataOnlyGQL,
    private aggregationDataOnlyGQL: AggregationDataOnlyGQL,
    private previewReportWithConfigsGQL: PreviewReportWithConfigsGQL,
    private graphqlModule: GraphQLModule,
    private queryParamsService: QueryParamsService,
    private branding: BrandingService,
    private router: Router,
    private apollo: PlusApollo,
    private periodService: PeriodService,
    private activatedRoute: ActivatedRoute,
    private colorScheme: ColorSchemeService,
  ) {
    super(initialState);
  }

  readonly period$ = this.select((state) => state.period);
  readonly setPeriod = this.updater((state, period: AdvancedPeriodOptions | 'Month of Year'): DashboardState => ({ ...state, period }));

  readonly customPeriod$ = this.select((state) => state.customPeriod);
  readonly setCustomPeriod = this.updater((state, customPeriod: Date[]): DashboardState => ({ ...state, customPeriod }));

  readonly month$ = this.select((state) => state.month);
  readonly setMonth = this.updater((state, month: string): DashboardState => ({ ...state, month }));

  readonly expandedFlows$ = this.select((state) => state.expandedFlows);
  readonly setExpandedFlows = this.updater((state, expandedFlows: ExpandedFlow[]): DashboardState => ({ ...state, expandedFlows }))

  readonly pipelinePeriod$ = this.select((state) => state.pipelinePeriod);
  readonly setPipelinePeriod = this.updater((state, pipelinePeriod: PipelinePeriod): DashboardState => ({ ...state, pipelinePeriod }));;

  readonly pipelineMetric$ = this.select((state) => state.pipelineMetric);
  readonly setPipelineMetric = this.updater((state, pipelineMetric: PipelineMetric): DashboardState => ({ ...state, pipelineMetric }));

  readonly sortEngineJobsBy$ = this.select((state) => state.sortEngineJobsBy);
  readonly setSortEngineJobsBy = this.updater((state, sortEngineJobsBy: SortBy): DashboardState => ({ ...state, sortEngineJobsBy }));

  readonly jobStage$ = this.select((state) => state.jobStage);
  readonly setJobStage = this.updater((state, jobStage: JobStage): DashboardState => ({ ...state, jobStage }));

  readonly status$ = this.select((state) => state.status);
  readonly setStatus = this.updater((state, status: 'open' | 'closed'): DashboardState => ({ ...state, status }));

  readonly source$ = this.select((state) => state.source);
  readonly setSource = this.updater((state, source: string): DashboardState => ({ ...state, source }));

  readonly jobOrigin$ = this.select((state) => state.jobOrigin);
  readonly setJobOrigin = this.updater((state, jobOrigin: string): DashboardState => ({ ...state, jobOrigin }));

  readonly estimateMethod$ = this.select((state) => state.estimateMethod);
  readonly setEstimateMethod = this.updater((state, estimateMethod: string): DashboardState => ({ ...state, estimateMethod }));

  readonly estimator$ = this.select((state) => state.estimator);
  readonly setEstimator = this.updater((state, estimator: string): DashboardState => ({ ...state, estimator }));

  readonly salesAgent$ = this.select((state) => state.salesAgent);
  readonly setSalesAgent = this.updater((state, salesAgent: string): DashboardState => ({ ...state, salesAgent }));

  readonly utmSource$ = this.select((state) => state.utmSource);
  readonly setUtmSource = this.updater((state, utmSource: string): DashboardState => ({ ...state, utmSource }));

  readonly utmTerm$ = this.select((state) => state.utmTerm);
  readonly setUtmTerm = this.updater((state, utmTerm: string): DashboardState => ({ ...state, utmTerm }));

  readonly utmMedium$ = this.select((state) => state.utmMedium);
  readonly setUtmMedium = this.updater((state, utmMedium: string): DashboardState => ({ ...state, utmMedium }));

  readonly utmCampaign$ = this.select((state) => state.utmCampaign);
  readonly setUtmCampaign = this.updater((state, utmCampaign: string): DashboardState => ({ ...state, utmCampaign }));

  readonly utmContent$ = this.select((state) => state.utmContent);
  readonly setUtmContent = this.updater((state, utmContent: string): DashboardState => ({ ...state, utmContent }));

  readonly groupLeadsBy$ = this.select((state) => state.groupLeadsBy);
  readonly setGroupLeadsBy = this.updater((state, groupLeadsBy: GroupLeadsBy): DashboardState => ({ ...state, groupLeadsBy }));

  readonly jobClosureReason$ = this.select((state) => state.jobClosureReason);
  readonly setJobClosureReason = this.updater((state, jobClosureReason: string): DashboardState => ({ ...state, jobClosureReason }));

  readonly attentionExplanation$ = this.select((state) => state.attentionExplanation);
  // eslint-disable-next-line max-len
  readonly setAttentionExplanation = this.updater((state, attentionExplanation: string): DashboardState => ({ ...state, attentionExplanation }));

  readonly jobClosureReasons$ = this.select((state) => state.jobClosureReasons);
  readonly attentionExplanations$ = this.select((state) => state.attentionExplanations);

  readonly selectedJobType$ = this.select(this.select({
    jobStage: this.jobStage$,
    status: this.status$,
    source: this.source$,
    jobOrigin: this.jobOrigin$,
    estimateMethod: this.estimateMethod$,
    jobClosureReason: this.jobClosureReason$,
    attentionExplanation: this.attentionExplanation$,
    estimator: this.estimator$,
    salesAgent: this.salesAgent$,
    utmSource: this.utmSource$,
    utmMedium: this.utmMedium$,
    utmCampaign: this.utmCampaign$,
    utmContent: this.utmContent$,
    utmTerm: this.utmTerm$,
  }, { debounce: true }), (selectedJobType) => {
    const jobTypeSelected = selectedJobTypeKeys.some((k) => selectedJobType[k] !== null);

    if (!jobTypeSelected) { return null; }

    return selectedJobType;
  });

  readonly engineJobs$ = this.select((state) => state.engineJobs);
  readonly engineJobsLoading$ = this.select((state) => state.engineJobsLoading);

  readonly engineJobsHasMore$ = this.select((state) => state.engineJobsHasMore);
  readonly engineJobsFetchingMore$ = this.select((state) => state.engineJobsFetchingMore);

  readonly comparedTo$ = this.select((state) => state.comparedTo);
  readonly setComparedTo = this.updater((state, comparedTo: ComparedTo): DashboardState => ({ ...state, comparedTo }));

  readonly currency$ = this.select((state) => state.currency);
  readonly setCurrency = this.updater((state, currency: 'USD' | 'CAD' | 'All'): DashboardState => ({ ...state, currency }));

  readonly lastReload$ = this.select((state) => state.lastReload);

  readonly isMonthMode$ = this.select((state) => state.period === 'Month of Year');

  readonly deltasEnabled$ = this.select((state) => state.period === 'Month of Year' || state.period === 'MTD');

  readonly customPeriodDisabled$ = this.select((state) => state.period !== 'Custom');

  readonly somethingLoading$ = this.select((state) => {

    const simpleLoadingProps: (keyof DashboardState)[] = [
      'averageInvoiceSizeLoading',
      'pastAverageInvoiceSizeLoading',
      'revenueAndLogisticsLoading',
      'pastRevenueAndLogisticsLoading',
      'activityLoading',
      'eventsCreatedLoading',
      'eventsHappeningLoading',
      'sourcesLoading',
      'pipelineLoading',
    ];

    const complexLoadingProps: (keyof DashboardState)[] = [
    ];

    return simpleLoadingProps.some((prop) => state[prop])
      || complexLoadingProps.some((prop) => [ 'initial', 'more'].includes(state[prop] as string));
  });

  readonly pastMonth$ = this.select(
    this.month$,
    this.comparedTo$,
    (month, comparedTo) => {

      const currentMonthDayJs = dayjs(month, 'MMMM YYYY');

      const substractUnit = comparedTo === 'Previous Month' ? 'month' : 'year';

      return currentMonthDayJs.subtract(1, substractUnit).format('MMMM YYYY');
    }
  );

  readonly unixPeriod$ = this.select(
    this.customPeriod$,
    (customPeriod) => {
      const [ startDate, endDate ] = customPeriod;
      return {
        start: startDate ? dayjs(startDate).startOf('day').unix() : getBeginningOfTimeUnix(),
        end: endDate ? dayjs(endDate).endOf('day').unix() : getEndOfTimeUnix(),
      };
    },
  );

  readonly pastUnixPeriod$ = this.select(
    this.period$,
    this.pastMonth$,
    this.comparedTo$,
    (period, pastMonth, comparedTo) => {

      if (period === 'Month of Year') {

        const pastMonthDayJs = dayjs(pastMonth, 'MMMM YYYY');

        return {
          start: pastMonthDayJs.startOf('month').unix(),
          end: pastMonthDayJs.endOf('month').unix(),
        };

      } else if (period === 'MTD') {

        const today = dayjs();

        const substractUnit = comparedTo === 'Previous Month' ? 'month' : 'year';

        const previousMonth = today.subtract(1, substractUnit);

        const daysInPreviousMonth = previousMonth.daysInMonth();

        const dayOfTheMonth = Math.min(today.date(), daysInPreviousMonth);

        return {
          start: previousMonth.startOf('month').unix(),
          end: previousMonth.date(dayOfTheMonth).endOf('day').unix(),
        }
      }
    },
  );

  readonly displayPeriod$ = this.select(
    this.period$,
    this.month$,
    this.customPeriod$,
    (period, month, customPeriod) => {

      if (period === 'Month of Year') {
        return month;
      }

      if (period !== 'Custom') {
        return period;
      }

      return customPeriod.map((date) => dayjs(date).format('MMM D, YYYY')).join(' - ');
    },
  );

  readonly invoiceSize$ = this.select((state) => this.calculateAverage(state, 'invoiceSize'));
  readonly averageInvoiceSize$ =this.select(this.invoiceSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly averageInvoiceSizeLoading$ = this.select((state) => state.averageInvoiceSizeLoading);
  readonly averageInvoiceSizeDescription$ = this.select(this.invoiceSize$, (data) => {

    const baseDescription = 'For all invoices finalized during the selected period, ';

    const extra = 'An invoice is considered finalized if it has been sent to the customer or opened on Pandadoc.';

    return this.getDescription({
      baseDescription,
      data,
      extra,
    });
  });
  readonly pastInvoiceSize$ = this.select((state) => this.calculateAverage(state, 'pastInvoiceSize'));
  readonly pastAverageInvoiceSize$ = this.select(this.pastInvoiceSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly pastAverageInvoiceSizeLoading$ = this.select((state) => state.pastAverageInvoiceSizeLoading);

  readonly jobSize$ = this.select((state) => this.calculateAverage(state, 'jobSize'));
  readonly averageJobSize$ = this.select(this.jobSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly averageJobSizeLoading$ = this.select((state) => state.averageJobSizeLoading);
  readonly averageJobSizeDescription$ = this.select(this.jobSize$, (data) => {
    return this.getDescription({
      baseDescription: 'For all leads that came in during the selected period (whether they have been booked or not), ',
      data,
    });
  });
  readonly pastJobSize$ = this.select((state) => this.calculateAverage(state, 'pastJobSize'));
  readonly pastAverageJobSize$ = this.select(this.pastJobSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly pastAverageJobSizeLoading$ = this.select((state) => state.pastAverageInvoiceSizeLoading);

  readonly bookingSize$ = this.select((state) => this.calculateAverage(state, 'bookingSize'));
  readonly averageBookingSize$ = this.select(this.bookingSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly averageBookingSizeLoading$ = this.select((state) => state.averageBookingSizeLoading);
  readonly averageBookingSizeDescription$ = this.select(this.bookingSize$, (data) => {
    return this.getDescription({
      baseDescription: 'For all jobs booked during the selected period, ',
      data,
    });
  });
  readonly pastBookingSize$ = this.select((state) => this.calculateAverage(state, 'pastBookingSize'));
  readonly pastAverageBookingSize$ = this.select(this.pastBookingSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly pastAverageBookingSizeLoading$ = this.select((state) => state.pastAverageBookingSizeLoading);

  readonly scheduledJobSize$ = this.select((state) => this.calculateAverage(state, 'scheduledJobSize'));
  readonly averageScheduledJobSize$ = this.select(this.scheduledJobSize$, (size) => ({ amount: size.average, currency: size.currency }));
  readonly averageScheduledJobSizeLoading$ = this.select((state) => state.averageScheduledJobSizeLoading);
  readonly averageScheduledJobSizeDescription$ = this.select(this.scheduledJobSize$, (data) => {

    const total = formatCurrency(data.total, data.currency);

    return `For all events scheduled to start in the selected period, 
      the sum of their net subtotal (${ total }) divided by the number of jobs containing such events (${data.count}). 
      `;

  });
  readonly pastScheduledJobSize$ = this.select((state) => this.calculateAverage(state, 'pastScheduledJobSize'));
  readonly pastAverageScheduledJobSize$ = this.select(
    this.pastScheduledJobSize$,
    (size) => ({ amount: size.average, currency: size.currency }),
  );
  readonly pastAverageScheduledJobSizeLoading$ = this.select((state) => state.pastAverageScheduledJobSizeLoading);

  readonly sources$ = this.select((state) => state.sources);
  readonly sourcesLoading$ = this.select((state) => state.sourcesLoading);

  readonly pipeline$ = this.select((state) => state.pipeline);
  readonly pipelineLoading$ = this.select((state) => state.pipelineLoading);

  readonly pipelineBreakdown$ = this.select(
    this.pipeline$,
    (rows) => {

      const groupByCount: Record<string, { jobCount: number; revenue: number }> = {};

      const stageCount: Partial<Record<JobStage, { jobCount: number; revenue: number }>> = {};

      const jobInfoCount: Record<string, { jobCount: number; revenue: number; }> = {};

      let jobsTotal = 0;

      let revenueTotal = 0;

      for (const [ jobData, rawJobCount, rawRevenue ] of rows) {

        const [ stage, group, jobClosureReason, attentionExplanation ] = jobData.split(';');

        const jobStage = stage as JobStage;

        const jobCount = parseInt(rawJobCount);
        const revenue = parseFloat(rawRevenue);

        groupByCount[group] = groupByCount[group] || {
          jobCount: 0,
          revenue: 0,
        };
        groupByCount[group].jobCount += jobCount;
        groupByCount[group].revenue += revenue;

        stageCount[jobStage] = stageCount[jobStage] || {
          jobCount: 0,
          revenue: 0,
        };
        stageCount[jobStage].jobCount += jobCount;
        stageCount[jobStage].revenue += revenue;

        jobsTotal += jobCount;
        revenueTotal += revenue;

        const isOpen = jobClosureReason === 'None';

        const status = isOpen ? 'open' : 'closed';

        const keyBasis = [ jobStage, status ];

        const stageStatusKey = keyBasis.join(delimiter);

        jobInfoCount[stageStatusKey] = jobInfoCount[stageStatusKey] || {
          jobCount: 0,
          revenue: 0,
        }

        jobInfoCount[stageStatusKey].jobCount += jobCount;
        jobInfoCount[stageStatusKey].revenue += revenue;

        const reason = isOpen ? attentionExplanation : jobClosureReason;

        keyBasis.push(reason);

        const stageStatusReasonKey = keyBasis.join(delimiter);

        jobInfoCount[stageStatusReasonKey] = jobInfoCount[stageStatusReasonKey] || {
          jobCount: 0,
          revenue: 0,
        }

        jobInfoCount[stageStatusReasonKey].jobCount += jobCount;
        jobInfoCount[stageStatusReasonKey].revenue += revenue;
      }

      const closedInvoiceKey = [ 'Invoice', 'closed'].join(delimiter);
      const completedInvoiceKey = [ 'Invoice', 'closed', 'complete' ].join(delimiter);

      if (jobInfoCount[closedInvoiceKey] && jobInfoCount[completedInvoiceKey]) {
        const incompleteKey = [ 'Invoice', 'closed', 'not-complete' ].join(delimiter);
        jobInfoCount[incompleteKey] = {
          jobCount: jobInfoCount[closedInvoiceKey].jobCount - jobInfoCount[completedInvoiceKey].jobCount,
          revenue: jobInfoCount[closedInvoiceKey].revenue - jobInfoCount[completedInvoiceKey].revenue,
        }
      }

      // Calculate cumulative stage count
      const cumulativeStageCount: Partial<Record<(typeof jobStages)[number], {
        jobCount: number;
        revenue: number;
      }>> = {};

      for (const jobStage of jobStages) {

        cumulativeStageCount[jobStage] = cumulativeStageCount[jobStage] || {
          jobCount: jobsTotal,
          revenue: revenueTotal,
        }

        if (!stageCount[jobStage]) { continue; }

        jobsTotal -= stageCount[jobStage].jobCount
        revenueTotal -= stageCount[jobStage].revenue;
      }

      return {
        groupByCount,
        jobInfoCount,
        cumulativeStageCount,
      };
    }
  );

  readonly pipelineChartData$ = this.select(
    this.pipelineBreakdown$,
    this.expandedFlows$,
    this.pipelineMetric$,
    this.groupLeadsBy$,
    this.selectedJobType$,
    this.currency$,
    this.attentionExplanations$,
    this.jobClosureReasons$,
    this.colorScheme.colorScheme.pipe(debounceTime(150)),
    (
      {
        groupByCount,
        cumulativeStageCount,
        jobInfoCount,
      },
      expandedFlows,
      metric,
      groupLeadsBy,
      selectedJobType,
      currency,
      attentionExplanations,
      jobClosureReasons,
    ) => {

    const {
      statusColumn,
      statusPriority,
      labelCount,
    } = calculateLabelCount(jobInfoCount, expandedFlows, attentionExplanations, jobClosureReasons);

    const column = {
      ...stageColumn,
      ...statusColumn,
    }

    const priority = {
      ...stagePriority,
      ...statusPriority,
    }

    const sortedGroups = [ ...Object.keys(groupByCount) ]
      .sort((a, b) => {
        if (metric === 'Job Count') {
          return groupByCount[b].jobCount - groupByCount[a].jobCount;
        }
        return groupByCount[b].revenue - groupByCount[a].revenue;
      });

    for (const [ index, group ] of sortedGroups.entries()) {;
      priority[group] = index;
    }

    const data: FlowInfo[] = [];

    // Enter flow from groups to lead
    for (const [ group, { jobCount, revenue } ] of Object.entries(groupByCount)) {

      const jobType: SelectedJobType = {
        jobStage: 'Lead',
      };

      switch (groupLeadsBy) {
        case "Source":
          jobType.source = group;
          break;
        case "Job Origin":
          jobType.jobOrigin = group;
          break;
        case "Estimate Method":
          jobType.estimateMethod = group;
          break;
        case "Estimator":
          jobType.estimator = group;
          break;
        case "Sales Agent":
          jobType.salesAgent = group;
          break;
        case "UTM Source":
          jobType.utmSource = group;
          break;
        case "UTM Medium":
          jobType.utmMedium = group;
          break;
        case "UTM Campaign":
          jobType.utmCampaign = group;
          break;
        case "UTM Content":
          jobType.utmContent = group;
          break;
        case "UTM Term":
          jobType.utmTerm = group;
          break;
      }

      const flow = metric === 'Job Count' ? jobCount : revenue;

      data.push({ from: group, to: 'Lead', flow, selectedJobType: jobType });
    }

    // Enter flow from one stage to the next
    for (let i = 0; i < jobStages.length; i++) {
      const jobStage = jobStages[i];
      const to = jobStages[i + 1];
      const metrics = cumulativeStageCount[to];

      if (!metrics) { continue; }

      const flow = metric === 'Job Count' ? metrics.jobCount : metrics.revenue;

      data.push({ from: jobStage, to, flow, selectedJobType: { jobStage } })
    }

    // Enter flow from stage to open/closed
    for (const [ to, { jobCount, revenue, jobType } ] of Object.entries(labelCount)) {

      const flow = metric === 'Job Count' ? jobCount : revenue;

      data.push({
        from: jobType.jobStage,
        to,
        flow,
        selectedJobType: jobType,
      });
    }

    const isName = [ 'Sales Agent', 'Estimator' ].includes(groupLeadsBy);

    const labels = {
      ...getLabelsFromCount(groupByCount, metric, currency, { isName }),
      ...getLabelsFromCount(cumulativeStageCount, metric, currency, { pluralizeLabel: true }),
      ...getLabelsFromCount(labelCount, metric, currency),
    };

    const fonts = [
      'Metropolis',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Helvetica Neue',
      'Arial',
      'sans-serif',
      'Apple Color Emoji',
      'Segoe UI Emoji',
      'Segoe UI Symbol',
    ];

    const documentStyle = getComputedStyle(document.documentElement);

    const dataSet: SankeyControllerDatasetOptions = {
      label: 'Pipeline',
      data,
      colorFrom: (c) => getColor((c.dataset.data[c.dataIndex] as any)?.selectedJobType, selectedJobType, documentStyle),
      colorTo: (c) => getColor((c.dataset.data[c.dataIndex] as any)?.selectedJobType, selectedJobType, documentStyle),
      hoverColorFrom: (c) => getColor((c.dataset.data[c.dataIndex] as any)?.selectedJobType, selectedJobType, documentStyle, true),
      hoverColorTo: (c) => getColor((c.dataset.data[c.dataIndex] as any)?.selectedJobType, selectedJobType, documentStyle, true),
      colorMode: 'gradient',
      labels,
      priority,
      column,
      size: 'max',
      parsing: {
        from: 'from',
        to: 'to',
        flow: 'flow',
      },
      // nodeWidth: 0,
      nodePadding: getMaxStackedFlows(data) * 5,
      borderWidth: 0,
      // modeX: 'even',
      color: documentStyle.getPropertyValue('--text-color'),
      font: {
        family: fonts.join(','),
        size: 12,
        style: 'normal',
        weight: 'normal',
        lineHeight: 1.2,
      },
    }

    return {
      datasets: [ dataSet ],
    };
  });

  readonly pipelineChartOptions$ = this.select(
    this.pipelineMetric$,
    this.currency$,
    (metric, currency) => {

      currency = currency === 'All' ? 'USD' : currency;

      const options: ChartOptions<'sankey'> = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        onHover(event, activeElements) {
          if (activeElements.length) {
            (event.native.target as any).style.cursor = 'pointer';
          } else {
            (event.native.target as any).style.cursor = 'default';
          }
        },
        plugins: {
          tooltip: {
            displayColors: false,
            callbacks: {
              label(context) {

                const data = context.raw as { flow: number; to: string; from: string; selectedJobType: SelectedJobType };

                const isUser = data.selectedJobType?.estimator || data.selectedJobType?.salesAgent;

                const from = isUser ? decodeUser(data.from).name : data.from;

                const amount = metric === 'Job Count' ? data.flow.toString() : formatCurrency(data.flow, currency);

                return `${from} to ${data.to}: ${amount}`;
              }
            }
          }
        }
      }

      return options;
    }
  )
  readonly actionableItems$ = this.select(
    this.pipelineBreakdown$,
    this.currency$,
    ({ jobInfoCount }, currency) => {

      if (currency === 'All') {
        currency = 'USD';
      }

      const itemKeys = [
        'openLeads',
        'openInvoices',
        'openEstimates',
      ] as const;

      const output: Partial<Record<typeof itemKeys[number], {
        show: boolean;
        revenue?: string;
        review?: () => void;
      }>> = {};

      const actionableStages = jobStages
        .filter((stage) => stage !== 'Booking');

      let hasActionableItems = false;

      for (const stage of actionableStages) {

        const jobInfoKey = [ stage, 'open' ].join(delimiter);

        const metrics = jobInfoCount[jobInfoKey];

        const outputKey = `open${stage}s`;

        output[outputKey] = {};

        if (!metrics || !metrics.jobCount) {
          output[outputKey].show = false;
          continue;
        }

        hasActionableItems = true;

        output[outputKey].show = true;

        if (metrics.revenue) {
          output[outputKey].revenue = formatCurrency(metrics.revenue, currency);
        }

        output[outputKey].review = () => {
          this.setJobStage(stage);
          this.setStatus('open');
          this.setAttentionExplanation(null);
          this.setJobClosureReason(null);
        }
      }

      return {
        ...output,
        hasActionableItems,
      };
    }
  )

  readonly pipelineChartHeader$ = this.select(
    this.period$,
    this.month$,
    this.customPeriod$,
    this.pipelinePeriod$,
    (period, month, customPeriod, pipelinePeriod) => {

      const whatHappenedTo = 'What happened to';

      const jobTypes: Record<PipelinePeriod, string> = {
          'Lead Created': 'leads created',
          Estimated: 'jobs estimated',
          Booked: 'jobs booked',
          Happening: 'jobs happening',
          Invoiced: 'jobs invoiced',
          Completed: 'jobs completed'
      }

      const jobsProcessed = jobTypes[pipelinePeriod];

      if (period === 'Month of Year') {
        return `${whatHappenedTo} ${jobsProcessed} in ${month}?`;
      }

      if (period === 'Custom') {
        const [ start, end ] = customPeriod;
        const startStr = dayjs(start).format('MMMM D');
        const endStr = dayjs(end).format('MMMM D');
        return `${whatHappenedTo} ${jobsProcessed} between ${startStr} and ${endStr}?`;
      }

      if (period === 'Since Last Run') {
        return `${whatHappenedTo} ${jobsProcessed} ${period.toLowerCase()}?`;
      }

      if (period === 'MTD Last Year') {
        return `${whatHappenedTo} ${jobsProcessed} MTD last year?`;
      }

      if (period.endsWith('hours') || period.endsWith('days') || period === 'Month After Next') {
        return `${whatHappenedTo} ${jobsProcessed} in the ${ period.toLowerCase() }?`;
      }

      const noPreposition: (typeof period)[] = [
        'Today',
        'Tomorrow',
        'Yesterday',
        'WTD',
        'MTD',
        'QTD',
        'YTD',
      ];

      if (noPreposition.includes(period) || period.startsWith('Last') || period.startsWith('Next') || period.startsWith('This')) {
        return `${whatHappenedTo} ${jobsProcessed} ${ period === period.toUpperCase() ? period : period.toLowerCase() }?`;
      }

      if (period === 'All Time') {
        return `${whatHappenedTo} all ${jobsProcessed}?`;
      }

      const pastAndFuture: (typeof period)[] = [
        'Past',
        'Future',
      ];

      if (pastAndFuture.includes(period) || period === period.toUpperCase()) {
        return `${whatHappenedTo} ${jobsProcessed} in the ${ period === period.toUpperCase() ? period : period.toLowerCase() }?`;
      }

      return `${whatHappenedTo} ${jobsProcessed} in ${ period.toLowerCase() }?`;
    }
  )

  readonly pipelineChartHeight$ = this.select(this.pipelineChartData$, ({ datasets }) => {

    const minHeight = 200;

    const [ { data } ] = datasets;

    if (!data.length) {
      return minHeight;
    }

    const maxStackedFlows = getMaxStackedFlows(datasets[0]?.data || []);

    return Math.max(maxStackedFlows * 45, minHeight) ;
  });

  readonly engineJobsTitle$ = this.select(
    this.selectedJobType$,
    this.attentionExplanations$,
    this.jobClosureReasons$,
    getTitle,
  );

  readonly conversionRate$ = this.select(
    this.pipelineBreakdown$,
    this.jobStage$,
    ({ cumulativeStageCount, jobInfoCount }, currentStage) => {

      const currentStageCount = cumulativeStageCount[currentStage].jobCount;

      if (currentStageCount === 0) {
        return 0;
      }

      const completedInvoicesKey = [ currentStage, 'closed', 'complete' ].join(delimiter);

      const nextStage = jobStages[ jobStages.indexOf(currentStage) + 1 ];

      const nextStageCount = currentStage === 'Invoice' ? jobInfoCount[completedInvoicesKey]?.jobCount || 0
        : cumulativeStageCount[nextStage]?.jobCount || 0;

      return (nextStageCount / currentStageCount) * 100;
    }
  );

  readonly engineJobsChips$ = this.select(
    this.pipelineBreakdown$,
    this.jobStage$,
    ({ cumulativeStageCount, jobInfoCount }, currentStage) => {

      const chips: {
        label: string;
        value: number;
        chipType: 'count' | 'percent' | 'currency';
      }[] = [];

      const leadCount = cumulativeStageCount['Lead']?.jobCount || 0;
      const estimateCount = cumulativeStageCount['Estimate']?.jobCount || 0;
      const bookingCount = cumulativeStageCount['Booking']?.jobCount || 0;
      const invoiceMetrics = cumulativeStageCount['Invoice'] || { jobCount: 0, revenue: 0 };
      const { jobCount: invoiceCount } = invoiceMetrics;

      const completedKey = [ 'Invoice', 'closed', 'complete' ].join(delimiter);

      const completedCount = jobInfoCount[completedKey]?.jobCount || 0;

      switch (currentStage) {
        case 'Lead':

          chips.push({
            label: 'Converted to estimate',
            value: this.getPercent({
              total: leadCount,
              converted: estimateCount,
            }),
            chipType: 'percent',
          });

          chips.push({
            label: 'Converted to booking',
            value: this.getPercent({
              total: leadCount,
              converted: bookingCount,
            }),
            chipType: 'percent',
          });

          chips.push({
            label: 'Converted to invoice',
            value: this.getPercent({
              total: leadCount,
              converted: invoiceCount,
            }),
            chipType: 'percent',
          });

          chips.push({
            label: 'Completed',
            value: this.getPercent({
              total: leadCount,
              converted: completedCount,
            }),
            chipType: 'percent',
          });

          break;
        case 'Estimate':

          chips.push({
            label: 'Estimated total',
            value: estimateCount,
            chipType: 'count',
          });

          chips.push({
            label: 'Converted to booking',
            value: this.getPercent({
              total: estimateCount,
              converted: bookingCount,
            }),
            chipType: 'percent',
          });

          chips.push({
            label: 'Converted to invoice',
            value: this.getPercent({
              total: estimateCount,
              converted: invoiceCount,
            }),
            chipType: 'percent',
          });

          chips.push({
            label: 'Completed',
            value: this.getPercent({
              total: estimateCount,
              converted: completedCount,
            }),
            chipType: 'percent',
          });

          break;
        case 'Booking':

          chips.push({
            label: 'Booked total',
            value: bookingCount,
            chipType: 'count',
          });

          chips.push({
            label: 'Converted to invoice',
            value: this.getPercent({
              total: bookingCount,
              converted: invoiceCount,
            }),
            chipType: 'percent',
          });

          chips.push({
            label: 'Completed',
            value: this.getPercent({
              total: bookingCount,
              converted: completedCount,
            }),
            chipType: 'percent',
          });
          break;

        case 'Invoice':

          chips.push({
            label: 'Invoiced total',
            value: invoiceCount,
            chipType: 'count',
          });

          const { revenue: invoiceRevenue } = invoiceMetrics;

          const averageInvoiceSize = invoiceCount ? invoiceRevenue / invoiceCount : 0;

          chips.push({
            label: 'Average invoice size',
            value: averageInvoiceSize,
            chipType: 'currency',
          });

          break;
        default:
        throw new Error(`Unhandled job stage ${currentStage}`);
      }

      return chips;
    }
  );

  readonly engineJobsBreadcrumbs$ = this.select(
    this.selectedJobType$,
    this.jobClosureReasons$,
    this.attentionExplanations$,
    (selectedJobType, jobClosureReasons, attentionExplanations) => {

      const styleClass = 'freya-link-button';

      const breadcrumbs: MenuItem[] = [];

      for (const jobStage of jobStages) {

        let label = pluralize(jobStage);

        if (jobStage === 'Lead') {
          label = `All ${label}`;
        }

        const command = () => {
          this.patchState({
            jobStage,
            status: null,
            attentionExplanation: null,
            jobClosureReason: null,
          });
        }

        breadcrumbs.push({ label, command, styleClass });

        if (jobStage === selectedJobType.jobStage) {
          break;
        }
      }

      if (selectedJobType.status) {

        const jobType = {
          jobStage: selectedJobType.jobStage,
          status: selectedJobType.status,
          attentionExplanation: null,
          jobClosureReason: null,
        };

        const command = () => {
          this.patchState(jobType);
        }

        breadcrumbs.push({
          label: getTitle(jobType, attentionExplanations, jobClosureReasons),
          command,
          styleClass,
        });
      }

      if (selectedJobType.attentionExplanation || selectedJobType.jobClosureReason) {


        if (
          selectedJobType.jobStage === 'Invoice' &&
          selectedJobType.status === 'closed' &&
          selectedJobType.jobClosureReason === 'complete'
        ) {
          breadcrumbs.pop();
        }

        breadcrumbs.push({
          label: getTitle(selectedJobType, attentionExplanations, jobClosureReasons, false),
          styleClass,
        });
      }

      const lastItem = breadcrumbs[breadcrumbs.length - 1];

      lastItem.command = undefined;
      lastItem.styleClass = undefined;

      return breadcrumbs;
    }
  );

  readonly sourcedData$ = this.select(this.sources$, this.currency$, (rows, selectedCurrency) => {

    const totalRevenue = rows.reduce((acc, row) => acc + parseFloat(row[2]), 0);

    const sourceMap: Record<string, {
      count: number;
      tooltip: string;
      amount: number;
      currency: string;
      percent: number;
    }> = {};

    let globalCurrency: string;

    for (const [ source, count, amount, currency ] of rows) {

      const currentValue = sourceMap[source];

      const defaultValue = {
        count: 0,
        amount: 0,
        percent: 0,
        tooltip: '',
        currency,
      };

      const value = currentValue || defaultValue;

      value.count += parseInt(count);
      value.amount += parseFloat(amount);
      value.percent = totalRevenue ? (value.amount / totalRevenue) * 100 : 0;
      value.tooltip = `${value.count} leads`;

      sourceMap[source] = value;

      if (!globalCurrency || globalCurrency !== baseEnvironment.defaultCurrency) {
        globalCurrency = currency;
      }
    }

    const sourceArray = Object.entries(sourceMap)
      .map(([ source, { count: jobCount, amount, percent, tooltip, currency } ]) => ({
        source,
        jobCount,
        tooltip,
        amount,
        currency: selectedCurrency === 'All' ? globalCurrency : currency,
        percent,
      }))
      .filter((s) => s.amount > 0);

    sourceArray.sort((a, b) => b.percent - a.percent);

    return sourceArray;
  });

  readonly comparedToText$ = this.select(
    this.period$,
    this.month$,
    this.pastMonth$,
    this.comparedTo$,
    (period, selectedMonth, pastMonth, comparedTo) => {

      // If user is in month mode and they selected a month other than the current one, show the name of the past month explicitly
      if (period === 'Month of Year') {

        const selectedMonthDate = dayjs(selectedMonth, 'MMMM YYYY');

        const isCurrentMonth = selectedMonthDate.isSame(dayjs(), 'month');

        if (!isCurrentMonth) {
          return `since ${pastMonth}`;
        }
      }

      return comparedTo === 'Previous Month' ? 'since last month' : 'since this month last year';
    },
  );

  readonly realizedRevenue$ = this.select((state) => state.realizedRevenue);
  readonly scheduledRevenue$ = this.select((state) => state.scheduledRevenue);
  readonly expectedRevenue$ = this.select((state) => state.expectedRevenue);
  readonly sharedRevenue$ = this.select((state) => state.sharedRevenue);
  readonly pastRealizedRevenue$ = this.select((state) => state.pastRealizedRevenue);
  readonly pastScheduledRevenue$ = this.select((state) => state.pastScheduledRevenue);
  readonly pastExpectedRevenue$ = this.select((state) => state.pastExpectedRevenue);
  readonly pastSharedRevenue$ = this.select((state) => state.pastSharedRevenue);

  readonly revenueAndLogisticsLoading$ = this.select((state) => state.revenueAndLogisticsLoading);
  readonly pastRevenueAndLogisticsLoading$ = this.select((state) => state.pastRevenueAndLogisticsLoading);

  readonly distanceBooked$ = this.select((state) => state.distanceBooked);
  readonly distanceCompleted$ = this.select((state) => state.distanceCompleted);
  readonly travelTimeBooked$ = this.select((state) => state.travelTimeBooked);
  readonly travelTimeCompleted$ = this.select((state) => state.travelTimeCompleted);

  readonly pastDistanceBooked$ = this.select((state) => state.pastDistanceBooked);
  readonly pastDistanceCompleted$ = this.select((state) => state.pastDistanceCompleted);
  readonly pastTravelTimeBooked$ = this.select((state) => state.pastTravelTimeBooked);
  readonly pastTravelTimeCompleted$ = this.select((state) => state.pastTravelTimeCompleted);

  readonly eventsCreated$ = this.select((state) => state.eventsCreated);
  readonly eventsCreatedLoading$ = this.select((state) => state.eventsCreatedLoading);
  readonly eventsCreatedError$ = this.select((state) => state.eventsCreatedError);
  readonly eventsHappening$ = this.select((state) => state.eventsHappening);
  readonly eventsHappeningLoading$ = this.select((state) => state.eventsHappeningLoading);
  readonly eventsHappeningError$ = this.select((state) => state.eventsHappeningError);

  readonly activityLoading$ = this.select((state) => state.activityLoading);

  readonly errors$ = this.select((state) => {

    const errors = Object.keys(state)
      .filter((key) => key.endsWith('Error'))
      .map((key) => state[key]);

    return uniq(errors.filter(Boolean));
  });

  readonly engineJobsInfo$ = this.select(
    this.selectedJobType$,
    this.pipelineBreakdown$,
    (selectedJobType, { jobInfoCount, cumulativeStageCount }) => {

      const {
        jobStage,
        status,
        attentionExplanation,
        jobClosureReason,
      } = selectedJobType;

      const jobTypes: {
        jobType: SelectedJobType,
        key: string,
        metrics: {
          jobCount: number;
          revenue: number;
        }
      }[] = [];

      if (!status) {

        // Open
        const openJobsKey = [ jobStage, 'open' ].join(delimiter);
        const openJobMetrics = jobInfoCount[openJobsKey];

        if (openJobMetrics?.jobCount > 0) {
          jobTypes.push({
            jobType: {
              jobStage,
              status: 'open',
              attentionExplanation: null,
              jobClosureReason: null,
            },
            key: openJobsKey,
            metrics: openJobMetrics,
          })
        }

        // Converted
        const nextStage = jobStages[ jobStages.indexOf(jobStage) + 1 ];

        const convertedJobType: SelectedJobType = {
          jobStage: nextStage,
          status: null,
          attentionExplanation: null,
          jobClosureReason: null,
        }

        let convertedJobKey: string = nextStage;
        let convertedJobMetrics = cumulativeStageCount[convertedJobKey]

        if (jobStage === 'Invoice') {
          convertedJobType.jobStage = 'Invoice';
          convertedJobType.status = 'closed';
          convertedJobType.jobClosureReason = 'complete';
          convertedJobKey = [ 'Invoice', 'closed', 'complete' ].join(delimiter);
          convertedJobMetrics = jobInfoCount[convertedJobKey];
        }

        if (convertedJobMetrics?.jobCount > 0) {
          jobTypes.push({
            jobType: convertedJobType,
            key: convertedJobKey,
            metrics: convertedJobMetrics,
          });
        }

        // Closed
        const closedJobsKeyBasis = [ jobStage, 'closed' ];

        if (jobStage === 'Invoice') {
          closedJobsKeyBasis.push('not-complete');
        }

        const closedJobsKey = closedJobsKeyBasis.join(delimiter);

        const closedJobMetrics = jobInfoCount[closedJobsKey];

        if (closedJobMetrics?.jobCount > 0) {
          jobTypes.push({
            jobType: {
              jobStage,
              status: 'closed',
              attentionExplanation: null,
              jobClosureReason: null,
            },
            key: closedJobsKey,
            metrics: closedJobMetrics,
          })
        }

      } else if (!attentionExplanation && !jobClosureReason) {

        const keyPrefix = [ jobStage, status  ].join(delimiter);

        const availableKeys = Object.keys(jobInfoCount)
          .filter((key) => key.startsWith(keyPrefix) && key.split(delimiter).length > 2);

        for (const key of availableKeys) {

          const reason = key.split(delimiter)[2];

          if (jobStage === 'Invoice' && status === 'closed' && [ 'complete', 'not-complete' ].includes(reason)) { continue; }

          const metrics = jobInfoCount[key];

          if (!metrics?.jobCount) { continue; }

          jobTypes.push({
            jobType: {
              jobStage,
              status,
              attentionExplanation: status === 'open' ? reason : null,
              jobClosureReason: status === 'closed' ? reason : null,
            },
            key,
            metrics,
          });
        }
      } else {

        const reason = status === 'open' ? attentionExplanation : jobClosureReason;

        const key = [ jobStage, status, reason ].join(delimiter);

        const metrics = jobInfoCount[key];

        jobTypes.push({
          jobType: selectedJobType,
          key,
          metrics,
        });
      }

      return {
        jobTypes,
        selectedJobType,
      };
    }
  );

  readonly engineJobSections = this.select(
    this.engineJobsInfo$,
    this.engineJobs$,
    this.attentionExplanations$,
    this.jobClosureReasons$,
    this.currency$,
    this.selectedJobType$,
    this.unixPeriod$,
    this.sortEngineJobsBy$,
    this.groupLeadsBy$,
    this.pipelinePeriod$,
    (
      { jobTypes },
      engineJobs,
      attentionExplanations,
      jobClosureReasons,
      currency,
      selectedJobType,
      { start, end },
      sortBy,
      groupLeadsBy,
      pipelinePeriod,
    ) => {

      const sections: {
        title?: string;
        jobs: SelectedJob[];
        jobType: SelectedJobType;
        button?: {
          label: string;
          command: () => void;
        };
        reportParams: AnyParams;
        loadingSkeletons: number[];
      }[] = [];

      for (const { jobType, key, metrics } of jobTypes) {

        const title = getTitle(jobType, attentionExplanations, jobClosureReasons, false);

        let fullTitle = title;

        const graphQLKey = encodeKeyForGraphQL(key);

        const rows = engineJobs[graphQLKey]?.data?.rows || [];

        const jobs = parseJobs(rows);

        let loadingSkeletons = new Array(ENGINE_JOBS_QUERY_LIMIT).fill(0);

        // Verify that sort by is taken into account
        const reportParams = this.getQueryParams({
          start,
          end,
          currency,
          report: 'engineJobs',
          opts: {
            selectedJobType: {
              ...selectedJobType,
              ...jobType,
            },
            sortBy,
            groupLeadsBy,
            pipelinePeriod,
          },
        });

        if (currency === 'All') {
          currency = 'USD';
        }

        if (metrics) {

          const revenue = formatCurrency(metrics.revenue, currency);

          fullTitle = `${ title } - ${ metrics.jobCount } - ${ revenue }`;

          loadingSkeletons = new Array(Math.min(metrics.jobCount, ENGINE_JOBS_QUERY_LIMIT)).fill(0);
        }

        const section: typeof sections[number] = {
          title: fullTitle,
          jobs,
          jobType,
          loadingSkeletons,
          reportParams,
        }

        const isLeaf = jobType.status && (jobType.attentionExplanation || jobType.jobClosureReason);

        if (isLeaf && metrics?.jobCount <= ENGINE_JOBS_QUERY_LIMIT) {
          sections.push(section);
          continue;
        }

        const goToJobs = () => {

          this.setJobStage(jobType.jobStage);

          if (jobType.status) {
            this.setStatus(jobType.status);
            this.setExpandedFlows([{
              jobStage: jobType.jobStage,
              status: jobType.status,
            }]);
          }

          if (jobType.attentionExplanation) {
            this.setAttentionExplanation(jobType.attentionExplanation);
          }

          if (jobType.jobClosureReason) {
            this.setJobClosureReason(jobType.jobClosureReason);
          }
        }

        const labelVerb = isLeaf ? 'View all' : 'Go to';

        section.button = {
          label: `${labelVerb} ${title}`,
          command: goToJobs,
        };

        sections.push(section);
      }

      if (sections.length === 1 && (selectedJobType.attentionExplanation || selectedJobType.jobClosureReason)) {
        const [ section ] = sections;
        section.title = undefined;
        section.button = undefined;
      }

      return sections;
    }
  );


  /**
   * TRIGGERS
   */

  readonly bottomReached$ = new Subject<DashboardPath>;

  readonly jobUpdated$ = new Subject<string>();

  readonly reloadDataTrigger$ = this.select(
    this.currency$,
    this.unixPeriod$,
    this.routeActivated$,
    this.graphqlModule.zone,
    (currency, { start, end }, path, zone) => {
      if (!zone || path !== 'revenue') return null;
      return { start, end, currency, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean)
  );

  readonly reloadPastDataTrigger$ = this.select(
    this.deltasEnabled$,
    this.currency$,
    this.pastUnixPeriod$,
    this.routeActivated$,
    this.graphqlModule.zone,
    (deltasEnabled, currency, pastUnixPeriod, path, zone) => {
      if (!zone || !deltasEnabled || path !== 'revenue') return null;
      const { start, end } = pastUnixPeriod || {};
      return { deltasEnabled, currency, start, end, path };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean)
  );

  readonly resetCustomPeriodTrigger$ = this.select(
    this.period$,
    this.month$,
    (period, month) => ({ period, month })
  ).pipe(filter((input) => input.period !== 'Custom'));

  readonly setQueryParamsTrigger$ = this.select(
    this.period$,
    this.customPeriod$,
    this.month$,
    this.comparedTo$,
    this.currency$,
    this.groupLeadsBy$,
    this.pipelineMetric$,
    this.pipelinePeriod$,
    this.jobStage$,
    this.status$,
    this.jobOrigin$,
    this.source$,
    this.estimateMethod$,
    this.jobClosureReason$,
    this.attentionExplanation$,
    this.estimator$,
    this.salesAgent$,
    this.utmSource$,
    this.utmTerm$,
    this.utmMedium$,
    this.utmCampaign$,
    this.utmContent$,
    (
      period,
      customPeriod,
      month,
      comparedTo,
      currency,
      groupLeadsBy,
      metric,
      pipelinePeriod,
      jobStage,
      status,
      jobOrigin,
      source,
      estimateMethod,
      jobClosureReason,
      attentionExplanation,
      estimator,
      salesAgent,
      utmSource,
      utmTerm,
      utmMedium,
      utmCampaign,
      utmContent,
    ) => ({
      period,
      customPeriod,
      month,
      comparedTo,
      currency,
      groupLeadsBy,
      metric,
      pipelinePeriod,
      jobStage,
      status,
      jobOrigin,
      source,
      estimateMethod,
      jobClosureReason,
      attentionExplanation,
      estimator,
      salesAgent,
      utmSource,
      utmTerm,
      utmMedium,
      utmCampaign,
      utmContent,
    }),
    { debounce: true },
  );

  readonly reloadPipelineTrigger$ = this.select(
    this.currency$,
    this.unixPeriod$,
    this.pipelineMetric$,
    this.pipelinePeriod$,
    this.source$,
    this.jobOrigin$,
    this.estimateMethod$,
    this.estimator$,
    this.salesAgent$,
    this.utmSource$,
    this.utmMedium$,
    this.utmCampaign$,
    this.utmContent$,
    this.utmTerm$,
    this.groupLeadsBy$,
    this.routeActivated$,
    this.jobUpdated$.pipe(startWith(undefined)),
    this.graphqlModule.zone,
    (
      currency,
      { start, end },
      metric,
      pipelinePeriod,
      source,
      jobOrigin,
      estimateMethod,
      estimator,
      salesAgent,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmTerm,
      groupLeadsBy,
      path,
      jobUpdated,
      zone,
    ) => {
      if(!zone) return null;
      return {
        start,
        end,
        currency,
        metric,
        pipelinePeriod,
        source,
        jobOrigin,
        estimateMethod,
        estimator,
        salesAgent,
        utmSource,
        utmMedium,
        utmCampaign,
        utmContent,
        utmTerm,
        groupLeadsBy,
        path,
        jobUpdated,
      };
    },
    { debounce: true },
  ).pipe(
    filter(Boolean),
    filter(({ path }) => path === 'engine'),
  );

  readonly reloadEngineJobsTrigger$ = this.select(
    this.currency$,
    this.unixPeriod$,
    this.pipelinePeriod$,
    this.sortEngineJobsBy$,
    this.engineJobsInfo$,
    this.source$,
    this.jobOrigin$,
    this.estimateMethod$,
    this.estimator$,
    this.salesAgent$,
    this.utmSource$,
    this.utmMedium$,
    this.utmCampaign$,
    this.utmContent$,
    this.utmTerm$,
    this.routeActivated$,
    this.jobUpdated$.pipe(startWith(undefined)),
    this.graphqlModule.zone,
    (
      currency,
      { start, end },
      pipelinePeriod,
      sortBy,
      { jobTypes, selectedJobType },
      source,
      jobOrigin,
      estimateMethod,
      estimator,
      salesAgent,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmTerm,
      path,
      jobUpdated,
    ) => {
      return {
        start,
        end,
        pipelinePeriod,
        currency,
        sortBy,
        jobTypes,
        selectedJobType,
        source,
        jobOrigin,
        estimateMethod,
        estimator,
        salesAgent,
        utmSource,
        utmMedium,
        utmCampaign,
        utmContent,
        utmTerm,
        path,
        jobUpdated,
      };
    },
    { debounce: true },
  ).pipe(
    filter(({ jobTypes, path }) => {
      return jobTypes.length && path === 'engine';
    }),
  );

  readonly filterChips$ = this.select(
    this.source$,
    this.jobOrigin$,
    this.estimateMethod$,
    this.estimator$,
    this.salesAgent$,
    this.utmSource$,
    this.utmTerm$,
    this.utmMedium$,
    this.utmCampaign$,
    this.utmContent$,
    (
      source,
      jobOrigin,
      estimateMethod,
      estimator,
      salesAgent,
      utmSource,
      utmTerm,
      utmMedium,
      utmCampaign,
      utmContent,
    ) => {

      const chips: {
        label: string;
        onRemove: () => void;
      }[] = [];

      if (source) {
        chips.push({
          label: `Source: ${ source }`,
          onRemove: () => this.setSource(null),
        });
      }

      if (jobOrigin) {
        chips.push({
          label: `Job Origin: ${ jobOrigin }`,
          onRemove: () => this.setJobOrigin(null),
        });
      }

      if (estimateMethod) {
        chips.push({
          label: `Estimate Method: ${ estimateMethod }`,
          onRemove: () => this.setEstimateMethod(null),
        });
      }

      if (estimator) {
        chips.push({
          label: `Estimator: ${ getUserName(estimator) }`,
          onRemove: () => this.setEstimator(null),
        });
      }

      if (salesAgent) {
        chips.push({
          label: `Sales Agent: ${ getUserName(salesAgent) }`,
          onRemove: () => this.setSalesAgent(null),
        });
      }

      if (utmSource) {
        chips.push({
          label: `UTM Source: ${ utmSource }`,
          onRemove: () => this.setUtmSource(null),
        });
      }

      if (utmTerm) {
        chips.push({
          label: `UTM Term: ${ utmTerm }`,
          onRemove: () => this.setUtmTerm(null),
        });
      }

      if (utmMedium) {
        chips.push({
          label: `UTM Medium: ${utmMedium}`,
          onRemove: () => this.setUtmMedium(null),
        });
      }

      if (utmCampaign) {
        chips.push({
          label: `UTM Campaign: ${utmCampaign}`,
          onRemove: () => this.setUtmCampaign(null),
        });
      }

      if (utmContent) {
        chips.push({
          label: `UTM Content: ${ utmContent }`,
          onRemove: () => this.setUtmContent(null),
        });
      }

      return chips;
    }
  )

  /**
   * VIEW MODELS
   */
  readonly settingsViewModel$ = this.select({
    comparedTo: this.comparedTo$,
    currency: this.currency$,
    period: this.period$,
    month: this.month$,
    customPeriod: this.customPeriod$,
    isMonthMode: this.isMonthMode$,
    showComparedTo: this.deltasEnabled$,
    customPeriodDisabled: this.customPeriodDisabled$,
  });

  readonly sharedViewModel$ = this.select({
    loading: this.somethingLoading$,
    lastReload: this.lastReload$,
    errors: this.errors$,
    displayPeriod: this.displayPeriod$,
    month: this.month$,
    metric: this.pipelineMetric$,
    period: this.pipelinePeriod$,
    groupLeadsBy: this.groupLeadsBy$,
  });

  readonly averageInvoiceSizeViewModel$ = this.select({
    average: this.averageInvoiceSize$,
    description: this.averageInvoiceSizeDescription$,
    queryParams: this.getReportQueryParams('averageInvoiceSize'),
    loading: this.averageInvoiceSizeLoading$,
    error: this.select((state) => state.averageInvoiceSizeError),
    delta: this.select(
      this.averageInvoiceSizeLoading$,
      this.pastAverageInvoiceSizeLoading$,
      this.averageInvoiceSize$,
      this.pastAverageInvoiceSize$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
  });

  readonly averageJobSizeViewModel$ = this.select({
    average: this.averageJobSize$,
    description: this.averageJobSizeDescription$,
    queryParams: this.getReportQueryParams('averageJobSize'),
    loading: this.averageJobSizeLoading$,
    error: this.select((state) => state.averageInvoiceSizeError),
    delta: this.select(
      this.averageJobSizeLoading$,
      this.pastAverageJobSizeLoading$,
      this.averageJobSize$,
      this.pastAverageJobSize$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    )
  });

  readonly averageBookingSizeViewModel$ = this.select({
    average: this.averageBookingSize$,
    description: this.averageBookingSizeDescription$,
    queryParams: this.getReportQueryParams('averageBookingSize'),
    loading: this.averageBookingSizeLoading$,
    error: this.select((state) => state.averageBookingSizeError),
    delta: this.select(
      this.averageBookingSizeLoading$,
      this.pastAverageBookingSizeLoading$,
      this.averageBookingSize$,
      this.pastAverageBookingSize$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    )
  });

  readonly averageScheduledJobSizeViewModel$ = this.select({
    average: this.averageScheduledJobSize$,
    description: this.averageScheduledJobSizeDescription$,
    queryParams: this.getReportQueryParams('averageScheduledJobSize'),
    loading: this.averageScheduledJobSizeLoading$,
    error: this.select((state) => state.averageScheduledJobSizeError),
    delta: this.select(
      this.averageScheduledJobSizeLoading$,
      this.pastAverageScheduledJobSizeLoading$,
      this.averageScheduledJobSize$,
      this.pastAverageScheduledJobSize$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
  });

  readonly sourcesViewModel$ = this.select({
    sources: this.sources$,
    data: this.sourcedData$,
    queryParams: this.getReportQueryParams('sources'),
    loading: this.sourcesLoading$,
    error: this.select((state) => state.sourcesError),
    isEmpty: this.select(
      this.sources$,
      this.sourcesLoading$,
      (data, loading) => !data.length && !loading,
    ),
    period: this.displayPeriod$,
  });

  readonly eventsViewModel$ = this.select({
    eventsCreated: this.select(
      this.eventsCreated$,
      this.currency$,
      (eventsCreated, currency) => this.formatEventData(eventsCreated, currency),
    ),
    createdLoading: this.eventsCreatedLoading$,
    createdEmpty: this.select(
      this.eventsCreated$,
      this.eventsCreatedLoading$,
      (created, loading) => !created.rows.length && !loading,
    ),
    eventsHappening: this.select(
      this.eventsHappening$,
      this.currency$,
      (eventsHappening, currency) => this.formatEventData(eventsHappening, currency),
    ),
    happeningLoading: this.eventsHappeningLoading$,
    happeningEmpty: this.select(
      this.eventsHappening$,
      this.eventsHappeningLoading$,
      (happening, loading) => !happening.rows.length && !loading,
    ),
    createdQueryParams: this.getReportQueryParams('eventsCreated'),
    happeningQueryParams: this.getReportQueryParams('eventsHappening'),
    error: this.select(
      this.eventsCreatedError$,
      this.eventsHappeningError$,
      (created, happening) => created || happening,
    ),
    eventsCreatedError: this.eventsCreatedError$,
    eventsHappeningError: this.eventsHappeningError$,
    period: this.displayPeriod$,
  });

  readonly activityViewModel$ = this.select({
    transactionsHappeningPerDay: this.select((state) => state.transactionsPaidPerDay),
    isMonthMode: this.isMonthMode$,
    period: this.unixPeriod$,
    eventsCreatedPerDay: this.select((state) => state.eventsCreatedPerDay),
    eventsHappeningPerDay: this.select((state) => state.eventsHappeningPerDay),
    transactionsPaidQueryParams: this.getReportQueryParams('transactionsPaid'),
    eventsCreatedQueryParams: this.getReportQueryParams('eventsCreatedPerDay'),
    eventsHappeningQueryParams: this.getReportQueryParams('eventsHappeningPerDay'),
    displayPeriod: this.displayPeriod$,
    loading: this.activityLoading$,
  });

  readonly revenueViewModel$ = this.select({
    realized: this.realizedRevenue$,
    realizedRevenueDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.realizedRevenue$,
      this.pastRealizedRevenue$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    scheduled: this.scheduledRevenue$,
    scheduledRevenueDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.scheduledRevenue$,
      this.pastScheduledRevenue$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    expected: this.expectedRevenue$,
    expectedRevenueDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.expectedRevenue$,
      this.pastExpectedRevenue$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    shared: this.sharedRevenue$,
    sharedRevenueDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.sharedRevenue$,
      this.pastSharedRevenue$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getCurrencyDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    showShared: this.select(
      this.sharedRevenue$,
      this.pastSharedRevenue$,
      (current, past) => current.amount || past.amount,
    ),
    queryParams: this.getReportQueryParams('revenueAndLogistics'),
    loading: this.select((state) => state.revenueAndLogisticsLoading),
    error: this.select((state) => state.revenueAndLogisticsError),
    period: this.displayPeriod$,
  });

  readonly logisticsViewModel$ = this.select({
    distanceBooked: this.distanceBooked$,
    distanceBookedDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.distanceBooked$,
      this.pastDistanceBooked$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getAmountDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    distanceCompleted: this.distanceCompleted$,
    distanceCompletedDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.distanceCompleted$,
      this.pastDistanceCompleted$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getAmountDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    travelTimeBooked: this.travelTimeBooked$,
    travelTimeBookedDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.travelTimeBooked$,
      this.pastTravelTimeBooked$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getAmountDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    travelTimeCompleted: this.travelTimeCompleted$,
    travelTimeCompletedDelta: this.select(
      this.revenueAndLogisticsLoading$,
      this.pastRevenueAndLogisticsLoading$,
      this.travelTimeCompleted$,
      this.pastTravelTimeCompleted$,
      (currentLoading, pastLoading, current, past) => ({
        ...this.getAmountDelta({ current, past }),
        loading: currentLoading || pastLoading,
      }),
    ),
    units: this.select((state) => state.units),
    queryParams: this.getReportQueryParams('revenueAndLogistics'),
    loading: this.revenueAndLogisticsLoading$,
    error: this.select((state) => state.revenueAndLogisticsError),
    period: this.displayPeriod$,
  });

  readonly deltaViewModel$ = this.select({
    comparedToText: this.comparedToText$,
    enabled: this.deltasEnabled$,
  });

  readonly pipelineViewModel$ = this.select({
    chartData: this.pipelineChartData$,
    chartHeight: this.pipelineChartHeight$,
    chartOptions: this.pipelineChartOptions$,
    isEmpty: this.select(
      this.pipeline$,
      this.pipelineLoading$,
      (data, loading) => !data.length && !loading,
    ),
    error: this.select((state) => state.pipelineError),
    queryParams: this.getReportQueryParams('pipeline'),
    loading: this.pipelineLoading$,
  });

  readonly engineJobsViewModel$ = this.select({
    title: this.engineJobsTitle$,
    breadcrumbs: this.engineJobsBreadcrumbs$,
    chips: this.engineJobsChips$,
    currency: this.select(this.currency$, ((currency) => currency === 'All' ? 'USD' : currency)),
    sortBy: this.sortEngineJobsBy$,
    conversionRate: this.conversionRate$,
    jobStage: this.jobStage$,
    sections: this.engineJobSections,
    loading: this.engineJobsLoading$,
    isEmpty: this.select(
      this.engineJobsInfo$,
      this.pipelineLoading$,
      ({ jobTypes }, loading) => !jobTypes.length && !loading,
    ),
    fetchingMore: this.engineJobsFetchingMore$,
  });

  /**
   * EFFECTS
   */

  readonly reloadAverageInvoiceSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadDataTrigger$,
    report: 'averageInvoiceSize',
    countKey: 'invoiceSizeCount',
    totalKey: 'invoiceSizeTotal',
    loadingKey: 'averageInvoiceSizeLoading',
    errorKey: 'averageInvoiceSizeError',
  });

  readonly reloadPastAverageInvoiceSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadPastDataTrigger$,
    report: 'averageInvoiceSize',
    countKey: 'pastInvoiceSizeCount',
    totalKey: 'pastInvoiceSizeTotal',
    loadingKey: 'pastAverageInvoiceSizeLoading',
    errorKey: 'averageInvoiceSizeError',
  });

  readonly reloadAverageJobSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadDataTrigger$,
    report: 'averageJobSize',
    countKey: 'jobSizeCount',
    totalKey: 'jobSizeTotal',

    loadingKey: 'averageJobSizeLoading',
    errorKey: 'averageJobSizeError',
  });

  readonly reloadPastAverageJobSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadPastDataTrigger$,
    report: 'averageJobSize',
    countKey: 'pastJobSizeCount',
    totalKey: 'pastJobSizeTotal',
    loadingKey: 'pastAverageJobSizeLoading',
    errorKey: 'averageJobSizeError',
  });

  readonly reloadAverageBookingSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadDataTrigger$,
    report: 'averageBookingSize',
    countKey: 'bookingSizeCount',
    totalKey: 'bookingSizeTotal',
    loadingKey: 'averageBookingSizeLoading',
    errorKey: 'averageBookingSizeError',
  });

  readonly reloadPastAverageBookingSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadPastDataTrigger$,
    report: 'averageBookingSize',
    countKey: 'pastBookingSizeCount',
    totalKey: 'pastBookingSizeTotal',
    loadingKey: 'pastAverageBookingSizeLoading',
    errorKey: 'averageBookingSizeError',
  });

  readonly reloadAverageScheduledJobSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadDataTrigger$,
    report: 'averageScheduledJobSize',
    countKey: 'scheduledJobSizeCount',
    totalKey: 'scheduledJobSizeTotal',
    loadingKey: 'averageScheduledJobSizeLoading',
    errorKey: 'averageScheduledJobSizeError',
  });

  readonly reloadPastAverageScheduledJobSize = this.getReloadAverageSizeEffect({
    trigger$: this.reloadPastDataTrigger$,
    report: 'averageScheduledJobSize',
    countKey: 'pastScheduledJobSizeCount',
    totalKey: 'pastScheduledJobSizeTotal',
    loadingKey: 'pastAverageScheduledJobSizeLoading',
    errorKey: 'pastAverageScheduledJobSizeError',
  });

  readonly reloadPipeline = this.effect(() => this.reloadPipelineTrigger$.pipe(
    map(({
      currency,
      start,
      end,
      source,
      jobOrigin,
      estimateMethod,
      estimator,
      salesAgent,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmTerm,
      groupLeadsBy,
      jobUpdated,
      pipelinePeriod,
    }) => ({
      queryVars: this.getQueryVars({
        start,
        end,
        currency,
        report: 'pipeline',
        opts: {
          selectedJobType: {
            source,
            jobOrigin,
            estimateMethod,
            estimator,
            salesAgent,
            utmSource,
            utmMedium,
            utmCampaign,
            utmContent,
            utmTerm,
          },
          groupLeadsBy,
          pipelinePeriod,
        }
      }),
      jobUpdated,
    })),
    tap(() => this.patchState({
      pipelineLoading: true,
      pipelineError: null,
    })),
    switchMap(({
      queryVars,
      jobUpdated,
    }) => this.previewReportWithConfigsGQL.fetch(queryVars, { fetchPolicy: jobUpdated ? 'network-only' : 'cache-first' }).pipe(tapResponse({
      next: (res) => {
        this.patchState(() => ({
          pipeline: res.data.previewReport.data.rows,
          jobClosureReasons: getJobClosureReasons(res.data.closureReasonsAndAttentionExplanations),
          attentionExplanations: getAttentionExplanations(res.data.closureReasonsAndAttentionExplanations),
          pipelineLoading: false,
          lastReload: currentTimeSeconds(),
        }));
      },
      error: (err: ApolloError) => {
        this.patchState({
          pipelineLoading: false,
          pipelineError: getApolloErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadEngineJobs = this.effect(() => this.reloadEngineJobsTrigger$.pipe(
    tap(() => this.patchState({
      engineJobsLoading: true,
      engineJobsError: null,
    })),
    switchMap((fetchEngineJobsInput) => this.fetchEngineJobs(fetchEngineJobsInput).pipe(
      tapResponse({
          next: (res) => {
            this.patchState({
              engineJobs: res.data,
              engineJobsLoading: false,
              lastReload: currentTimeSeconds(),
              engineJobsHasMore: this.getEngineJobsHasMore(res.data, fetchEngineJobsInput.jobTypes),
            });
          },
          error: (err: ApolloError) => {
            this.patchState({
              engineJobsLoading: false,
              engineJobsError: getApolloErrorMessage(err),
            });
          },
        }),
    )),
  ));

  readonly fetchMoreEngineJobs = this.effect(() => this.bottomReached$.pipe(
    debounceTime(150),
    withLatestFrom(this.reloadEngineJobsTrigger$, this.engineJobsHasMore$, this.engineJobs$),
    filter(([ path, { selectedJobType }, hasMore ]) => {

      if (path !== 'engine' || !hasMore) {
        return false;
      }
      return Boolean(selectedJobType.attentionExplanation || selectedJobType.jobClosureReason);
    }),
    tap(() => this.patchState({
      engineJobsFetchingMore: true,
      engineJobsError: null,
    })),
    map(([ _path, triggerData, _hasMore, currentJobs ]) => {

      const { selectedJobType: {
        jobStage,
        status,
        jobClosureReason,
        attentionExplanation,
      } } = triggerData;

      const keyBasis = [ jobStage, status ] as string[];

      if (status === 'open') {
        keyBasis.push(attentionExplanation);
      } else {
        keyBasis.push(jobClosureReason);
      }

      const key = keyBasis.join(delimiter);

      const graphQLKey = encodeKeyForGraphQL(key);

      const rows = currentJobs[graphQLKey]?.data?.rows || [];

      return {
        queryVars: {
          ...triggerData,
          skip: rows.length,
        },
        graphQLKey,
        currentJobs,
      }
    }),
    switchMap(({ queryVars, currentJobs, graphQLKey }) => this.fetchEngineJobs(queryVars).pipe(tapResponse({
      next: (res) => {
        this.patchState({
          engineJobs: mergeJobs({
            currentJobs,
            newJobs: res.data,
            graphQLKey,
          }),
          engineJobsFetchingMore: false,
          engineJobsHasMore: !((res.data[graphQLKey]?.data?.rows || []).length < ENGINE_JOBS_QUERY_LIMIT),
          lastReload: currentTimeSeconds(),
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          engineJobsFetchingMore: false,
          engineJobsError: getApolloErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadEventsCreated = this.effect(() => this.reloadDataTrigger$.pipe(
    map(({ currency, start, end }) => this.getQueryVars({ start, end, currency, report: 'eventsCreated' })),
    tap(() => this.patchState({
      eventsCreatedLoading: true,
      eventsCreatedError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { headers, rows } = res.data.previewReport.data;
        this.patchState({
          eventsCreated: { headers, rows },
          eventsCreatedLoading: false,
          lastReload: currentTimeSeconds(),
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          eventsCreatedLoading: false,
          eventsCreatedError: getApolloErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadEventsHappening = this.effect(() => this.reloadDataTrigger$.pipe(
    map(({ currency, start, end }) => this.getQueryVars({ start, end, currency, report: 'eventsHappening' })),
    tap(() => this.patchState({
      eventsHappeningLoading: true,
      eventsHappeningError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(tapResponse({
      next: (res) => {
        const { rows, headers } = res.data.previewReport.data;

        this.patchState({
          eventsHappening: {
            headers: headers.length ? headers : placeholderEventHeaders,
            rows,
          },
          eventsHappeningLoading: false,
          lastReload: currentTimeSeconds(),
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          eventsHappeningLoading: false,
          eventsCreatedError: getApolloErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadActivity = this.effect(() => this.reloadDataTrigger$.pipe(
    tap(() => this.patchState({
      activityLoading: true,
      activityError: null,
    })),
    switchMap(({ start, end, currency }) => this.fetchActivity({ start, end, currency }).pipe(tapResponse({
      next: (res) => {
        this.patchState({
          transactionsPaidPerDay: res.data.transactionsPaid.data.rows,
          eventsCreatedPerDay: res.data.eventsCreated.data.rows,
          eventsHappeningPerDay: res.data.eventsHappening.data.rows,
          activityLoading: false,
          lastReload: currentTimeSeconds(),
        });
      },
      error: (err: ApolloError) => {
        this.patchState({
          activityLoading: false,
          activityError: getApolloErrorMessage(err),
        });
      },
    }))),
  ));

  readonly reloadRevenueAndLogistics = this.effect(() => this.reloadDataTrigger$.pipe(
    map(({ currency, start, end }) => this.getQueryVars({
      start,
      end,
      currency,
      report: 'revenueAndLogistics',
    })),
    tap(() => this.patchState({
      revenueAndLogisticsLoading: true,
      revenueAndLogisticsError: null,
    })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(
      withLatestFrom(of(queryVars)),
      map(([ res, queryVars ]) => getRevenueAndLogisticsTotals(res, queryVars.filter.job?.currency)),
      tapResponse({
        next: ({
          realizedRevenue,
          scheduledRevenue,
          expectedRevenue,
          sharedRevenue,
          distanceBooked,
          distanceCompleted,
          travelTimeBooked,
          travelTimeCompleted,
        }) => this.patchState({
          realizedRevenue,
          scheduledRevenue,
          expectedRevenue,
          sharedRevenue,
          distanceBooked,
          distanceCompleted,
          travelTimeBooked,
          travelTimeCompleted,
          revenueAndLogisticsLoading: false,
          lastReload: currentTimeSeconds(),
        }),
        error: (err: ApolloError) => this.patchState({
          revenueAndLogisticsLoading: false,
          revenueAndLogisticsError: getApolloErrorMessage(err),
        }),
      }),
    )),
  ));

  readonly reloadPastRevenueAndLogistics = this.effect(() => this.reloadPastDataTrigger$.pipe(
    map(({ currency, start, end }) => this.getQueryVars({
      start,
      end,
      currency,
      report: 'revenueAndLogistics',
    })),
    tap(() => this.patchState({ pastRevenueAndLogisticsLoading: true })),
    switchMap((queryVars) => this.dataOnlyGQL.fetch(queryVars).pipe(
      withLatestFrom(of(queryVars)),
      map(([ res, queryVars ]) => getRevenueAndLogisticsTotals(res, queryVars.filter.job?.currency)),
      tapResponse({
        next: ({
          realizedRevenue: pastRealizedRevenue,
          scheduledRevenue: pastScheduledRevenue,
          expectedRevenue: pastExpectedRevenue,
          sharedRevenue: pastSharedRevenue,
          distanceBooked: pastDistanceBooked,
          distanceCompleted: pastDistanceCompleted,
          travelTimeBooked: pastTravelTimeBooked,
          travelTimeCompleted: pastTravelTimeCompleted,
        }) => this.patchState({
          pastRealizedRevenue,
          pastScheduledRevenue,
          pastExpectedRevenue,
          pastSharedRevenue,
          pastDistanceBooked,
          pastDistanceCompleted,
          pastTravelTimeBooked,
          pastTravelTimeCompleted,
          pastRevenueAndLogisticsLoading: false,
          lastReload: currentTimeSeconds(),
        }),
        error: (err: ApolloError) => this.patchState({
          revenueAndLogisticsLoading: false,
          revenueAndLogisticsError: getApolloErrorMessage(err),
        }),
      }),
    )),
  ));

  readonly resetCustomPeriod = this.effect(() => this.resetCustomPeriodTrigger$.pipe(
    tap((input: ExtractObservableType<typeof this.resetCustomPeriodTrigger$>) => {
      const { month, period } = input;
      if (period === 'Month of Year') {
        const monthDayJs = dayjs(month, 'MMMM YYYY');
        this.setCustomPeriod([
          monthDayJs.startOf('month').toDate(),
          monthDayJs.endOf('month').toDate(),
        ]);
      } else {
        this.setCustomPeriod(this.periodService.getPeriodAsDateArray(period));
      }
    }),
  ));

  readonly resetTicks = this.effect(() => this.activityViewModel$.pipe(
    tap((activity: ExtractObservableType<typeof this.activityViewModel$>) => {

      const ticks = transactionVolumeChartOptions.scales.y.ticks;

      const allValues = [
        ...activity.transactionsHappeningPerDay.map(([ _label, value ]) => parseFloat(value)),
        ...activity.eventsCreatedPerDay.map(([ _label, value ]) => parseFloat(value)),
        ...activity.eventsHappeningPerDay.map(([ _label, value ]) => parseFloat(value)),
      ];

      const min = Math.min(...allValues);
      const max = Math.max(...allValues);

      const finiteMin = isFinite(min) ? min : 1;
      const finiteMax = isFinite(max) ? max : 1;

      const nSteps = 4;

      // @ts-ignore, Doesn't match the updated type for v3 but seems to be backwards compatible
      ticks.min = finiteMin;

      // @ts-ignore, Doesn't match the updated type for v3 but seems to be backwards compatible
      ticks.stepSize = ((finiteMax - finiteMin) || 1) / nSteps;
    }),
  ));

  readonly resetUnits = this.effect(() => this.branding.currentBranding.asObservable().pipe(
    map((branding) => {

      const countryCode = branding.country || environment.defaultCountry;
      const country = jurisdictions.find((j) => j.country === countryCode);
      return country?.units || environment.defaultUnits;
    }),
    tap(( units: DistanceUnit ) => this.patchState({ units })),
  ));

  readonly setQueryParams = this.effect(() => this.setQueryParamsTrigger$.pipe(
    skip(1),
    tap((input: ExtractObservableType<typeof this.setQueryParamsTrigger$>) => {

      const queryParams: Params = {};

      const inputKeys: (keyof {
        [K in keyof typeof input as typeof input[K] extends string ? K : never ]: string;
      })[] = [
        'currency',
        'period',
        'groupLeadsBy',
        'metric',
        'pipelinePeriod',
        'jobStage',
        'status',
        'source',
        'jobOrigin',
        'estimateMethod',
        'jobClosureReason',
        'attentionExplanation',
        'estimator',
        'salesAgent',
        'utmSource',
        'utmMedium',
        'utmCampaign',
        'utmContent',
        'utmTerm',
      ];

      for (const key of inputKeys) {
        if (input[key]) {
          queryParams[key] = input[key].replaceAll(' ', '-');
        }
      }

      if (input.period === 'Month of Year') {
        queryParams.month = input.month.replaceAll(' ', '-');
      } else if (input.period === 'Custom') {
        queryParams.customPeriod = input.customPeriod.map((date) => dayjs(date).format('YYYY-MM-DD')).join(';');
      }

      if (input.period === 'Month of Year' || input.period === 'MTD') {
        queryParams.comparedTo = input.comparedTo.replaceAll(' ', '-');
      }

      const searchParams = new URLSearchParams(queryParams).toString();

      const newUrl = `${location.pathname}?${searchParams}`;

      history.replaceState(null, '', newUrl);
    }),
  ));

  readonly storeComparedToInLocalStorage = this.effect(() => this.comparedTo$.pipe(
    skip(1),
    tap((comparedTo: ExtractObservableType<typeof this.comparedTo$>) => {
      localStorage.setItem(comparedToLsKey, comparedTo);
    }),
  ));

  getReloadAverageSizeEffect(input: {
    trigger$: Observable<any>;
    report: DashboardReport;
    loadingKey: KeyOfEndingWith<DashboardState, 'Loading'>;
    errorKey: KeyOfEndingWith<DashboardState, 'Error'>;
    countKey: KeyOfEndingWith<DashboardState, 'Count'>;
    totalKey: KeyOfEndingWith<DashboardState, 'Total'>;
  }
  ) {

    const {
      trigger$,
      report,
      loadingKey,
      errorKey,
      countKey,
      totalKey,
    } = input;

    return this.effect(() => trigger$.pipe(
      map(({ currency, start, end }) => this.getQueryVars({ start, end, currency, report })),
        tap(() => this.patchState({ [loadingKey]: true })),
        switchMap((queryVars) => this.aggregationDataOnlyGQL.fetch(queryVars).pipe(
          map((res) => getCountAndTotal(res)),
          tapResponse({
            next: ({ count, total }) => this.patchState({
              [loadingKey]: false,
              [countKey]: count,
              [totalKey]: total,
              lastReload: currentTimeSeconds(),
            }),
            error: (err: ApolloError) => this.patchState({
              [loadingKey]: false,
              [errorKey]: getApolloErrorMessage(err),
            }),
          }),
      )),
    ));

  }

  /**
   * Returns query variables for any report that serves as a data source for the dashboard.
   * Used by both dashboard widgets and their respective links to the underlying report,
   * ensuring that the link always points to the same data as the widget.
   */
  getQueryVars(input: {
    start: number;
    end: number;
    currency: string;
    report: DashboardReport;
    opts?: {
      sortBy?: string;
      selectedJobType?: SelectedJobType;
      groupLeadsBy?: GroupLeadsBy;
      skip?: number;
      pipelinePeriod?: PipelinePeriod;
    },
  }): PreviewReportQueryVariables {

    const { start, end, currency, report } = input;

    switch (report) {
      case 'averageInvoiceSize':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency),
          periodType: 'invoiceFinalizedAt',
          groupBy: 'invoice',
          dataColumns: [ ],
          aggregationColumns: [
            'invoiceDiscountedSubTotalSum',
            'invoiceCount',
            'invoiceDiscountedSubTotalAvg',
            'currency',
          ],
          performance: 'skip-data',
          // We do not want currency signs on amounts
          linkFormat: 'sheets',
        }
      case 'averageJobSize':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency),
          periodType: 'jobCreatedAt',
          groupBy: 'job',
          dataColumns: [ ],
          aggregationColumns: [
            'discountedSubTotalSum',
            'count',
            'discountedSubTotalAvg',
            'currency',
          ],
          performance: 'skip-data',
          // We do not want currency signs on amounts
          linkFormat: 'sheets',
        }
      case 'averageBookingSize':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: {
              getJobsOfState: [ 'open', 'closed' ],
              getJobsOfStage: [ 'booking', 'invoice' ],
            },
            event: {
              getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ],
            },
          }),
          periodType: 'jobBookingStageDate',
          groupBy: 'job',
          dataColumns: [ ],
          aggregationColumns: [
            'discountedSubTotalSum',
            'count',
            'discountedSubTotalAvg',
            'currency',
          ],
          performance: 'skip-data',
          // We do not want currency signs on amounts
          linkFormat: 'sheets',
        }
      case 'averageScheduledJobSize':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: {
              getJobsOfState: [ 'open', 'closed' ],
              getJobsOfStage: [ 'booking', 'invoice' ],
            },
            event: {
              getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ],
            },
          }),
          periodType: 'eventStart',
          groupBy: 'job',
          dataColumns: [ ],
          aggregationColumns: [
            'discountedSubTotalSum',
            'count',
            'discountedSubTotalAvg',
            'currency',
          ],
          performance: 'skip-data',
          // We do not want currency signs on amounts
          linkFormat: 'sheets',
        }
      case 'sources':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'jobCreatedAt',
          groupBy: 'source',
          dataColumns: [ 'source', 'jobCount', 'discountedSubTotal', 'currency' ],
          aggregationColumns: [],
          performance: 'skip-count',
          // We do not want currency signs on amounts
          linkFormat: 'sheets',
        };
      case 'eventsCreated':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventCreatedAt',
          groupBy: 'eventType',
          dataColumns: [
            'eventType',
            'discountedSubTotal',
            'eventCount',
            'averageDiscountedSubTotalPerEvent',
            'currency',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
        };
      case 'eventsHappening':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventStart',
          groupBy: 'eventType',
          dataColumns: [
            'eventType',
            'discountedSubTotal',
            'eventCount',
            'averageDiscountedSubTotalPerEvent',
            'currency',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
      };
      case 'revenueAndLogistics':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventEnd',
          groupBy: 'none',
          dataColumns: [
            'realizedRevenue',
            'unrealizedRevenue',
            'discountedSubTotal',
            'sharedRevenue',
            'distanceBooked',
            'distanceCompleted',
            'travelTimeBooked',
            'travelTimeCompleted',
            'currency',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
        };
      case 'leadConversions':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [] },
          }),
          periodType: 'jobCreatedAt',
          groupBy: 'jobOrigin',
          dataColumns: [
            'jobOrigin',
            'jobCount',
            'convertedJobCount',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
        };
      case 'oseConversion':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: { hasCompletedEventOfType: [ 'estimating' ] },
            event: { getEventsOfStatus: [] }
          }),
          periodType: 'jobCreatedAt',
          groupBy: 'jobStage',
          dataColumns: [
            'jobStage',
            'jobCount',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
        };
      case 'veConversion':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: { hasCompletedEventOfType: [ 'virtualEstimate' ] },
            event: { getEventsOfStatus: [] }
          }),
          periodType: 'jobCreatedAt',
          groupBy: 'jobStage',
          dataColumns: [
            'jobStage',
            'jobCount',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
        };
      case 'estimateConversion':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            job: { hasCompletedEventOfType: [
              'estimating',
              'virtualEstimate',
            ] },
            event: { getEventsOfStatus: [] }
          }),
          periodType: 'jobCreatedAt',
          groupBy: 'jobStage',
          dataColumns: [
            'jobStage',
            'jobCount',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
        };
      case 'transactionsPaid':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency),
          periodType: 'transactionPaidAt',
          groupBy: 'transactionPaidDate',
          dataColumns: [
            'transactionPaidDate',
            'transactionPaidTotal',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
        };
      case 'eventsHappeningPerDay':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventStart',
          groupBy: 'eventBookingDate',
          dataColumns: [
            'eventBookingDate',
            'discountedSubTotal',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
          sort: 'eventBookingDate ASC'
        };
      case 'eventsCreatedPerDay':
        return {
          skip: 0,
          limit: -1,
          start,
          end,
          filter: mergeInDefaultFilters(currency, {
            event: { getEventsOfStatus: [ 'booked', 'confirmed', 'completed' ] },
            charge: { excludeDamages: true },
          }),
          periodType: 'eventCreatedAt',
          groupBy: 'eventCreatedAt',
          dataColumns: [
            'eventCreatedAt',
            'discountedSubTotal',
          ],
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'sheets',
        };
      case 'pipeline':
      case 'engineJobs':

        const { opts: {
          selectedJobType,
          groupLeadsBy,
          skip,
          sortBy,
          pipelinePeriod,
        } = {} } = input;

        if (isNil(selectedJobType)) {
          throw new Error(`Dashboard report ${report} requires selectedJobType but none was provided`);
        }

        // Filters that will apply to both the pipeline chart and the preview section
        const engineJobFilters: PreviewReportFilter['job'] = {
          sources: has(selectedJobType, 'source', 'Not specified') ? [ selectedJobType.source] : [],
          origins: has(selectedJobType, 'jobOrigin', 'Not specified') ? [ selectedJobType.jobOrigin ] : [],
          estimatorIds: has(selectedJobType, 'estimator') ? [ decodeUser(selectedJobType.estimator).id ] : [],
          salesAgentIds: has(selectedJobType, 'salesAgent') ? [ decodeUser(selectedJobType.salesAgent).id ] : [],
          estimateMethods: has(selectedJobType, 'estimateMethod') ? [ selectedJobType.estimateMethod ] : [],
          utmSources: has(selectedJobType, 'utmSource', 'Not specified') ? [ selectedJobType.utmSource ] : [],
          utmMedia: has(selectedJobType, 'utmMedium', 'Not specified') ? [ selectedJobType.utmMedium ] : [],
          utmCampaigns: has(selectedJobType, 'utmCampaign', 'Not specified') ? [ selectedJobType.utmCampaign ] : [],
          utmContents: has(selectedJobType, 'utmContent', 'Not specified') ? [ selectedJobType.utmContent ] : [],
          utmTerms: has(selectedJobType, 'utmTerm', 'Not specified') ? [ selectedJobType.utmTerm ] : [],
          noSource: selectedJobType.source === 'Not specified',
          noOrigin: selectedJobType.jobOrigin === 'Not specified',
          noEstimateMethod: selectedJobType.estimateMethod === 'None',
          noEstimator: selectedJobType.estimator === 'None',
          noSalesAgent: selectedJobType.salesAgent === 'None',
          noUtmSource: selectedJobType.utmSource === 'Not specified',
          noUtmMedium: selectedJobType.utmMedium === 'Not specified',
          noUtmCampaign: selectedJobType.utmCampaign === 'Not specified',
          noUtmContents: selectedJobType.utmContent === 'Not specified',
          noUtmTerm: selectedJobType.utmTerm === 'Not specified',
          currency: currency === 'All' ? undefined : currency,
        };

        if (pipelinePeriod === 'Completed') {
          engineJobFilters.closedReason = 'complete';
        }

        const engineEventFilters: PreviewReportFilter['event'] = {
          getEventsOfType: getEventTypeFilter(pipelinePeriod),
        };

        const engineChargeFilter: PreviewReportFilter['charge'] = {
          eventStatuses: [ 'required', 'pending', 'booked', 'confirmed', 'completed' ],
        }

        const groupLeadsByMap: Record<GroupLeadsBy, string> = {
          'Source': 'jobStageStatusSource',
          'Job Origin': 'jobStageStatusOrigin',
          'Estimate Method': 'jobStageStatusEstimateMethod',
          'Estimator': 'jobStageStatusEstimator',
          'Sales Agent': 'jobStageStatusSalesAgent',
          'UTM Source': 'jobStageStatusUTMSource',
          'UTM Medium': 'jobStageStatusUTMMedium',
          'UTM Campaign': 'jobStageStatusUTMCampaign',
          'UTM Content': 'jobStageStatusUTMContent',
          'UTM Term': 'jobStageStatusUTMTerm',
        };

        const groupBy = groupLeadsByMap[groupLeadsBy];

        if (report === 'pipeline') {
          return {
            skip: 0,
            limit: -1,
            start,
            end,
            filter: {
              job: {
                ...engineJobFilters,
                getJobsOfState: [ 'open', 'closed' ],
                getJobsOfStage: [ 'lead', 'estimate', 'booking', 'invoice' ],
              },
              event: {
                ...engineEventFilters,
              },
              charge: {
                ...engineChargeFilter,
              }
            },
            periodType: getPeriodTypeForPipelinePeriod(pipelinePeriod),
            groupBy,
            dataColumns: [ groupBy, 'jobCount', 'discountedSubTotal'],
            aggregationColumns: [],
            performance: 'skip-count',
            linkFormat: 'sheets',
          };
        }

        const engineJobsDataColumns = [ ...selectedJobsColumns ] as string[];

        let sort: string | undefined = undefined;

        if (sortBy) {

          sort = sortByMap[sortBy];

          const [ sortColumn ] = sort.split(' ');

          if (!engineJobsDataColumns.includes(sortColumn)) {
            engineJobsDataColumns.push(sortColumn);
          }
        }

        const engineJobsQueryVars: Pick<PreviewReportQueryVariables,
            | 'periodType'
            |  'groupBy'
            |  'dataColumns'
            |  'aggregationColumns'
            |  'performance'
            |  'linkFormat'
            |  'sort'
            > = {
          periodType: getPeriodTypeForPipelinePeriod(pipelinePeriod),
          groupBy: 'job',
          dataColumns: engineJobsDataColumns,
          aggregationColumns: [],
          performance: 'skip-count',
          linkFormat: 'web',
          sort,
        };

        const excludeComplete = selectedJobType.jobStage === 'Invoice'
          && selectedJobType.status === 'closed'
          && selectedJobType.jobClosureReason !== 'complete';

        const isSpecificStage = Boolean(selectedJobType.status || selectedJobType.jobClosureReason || selectedJobType.attentionExplanation);

        return {
          skip,
          limit: ENGINE_JOBS_QUERY_LIMIT,
          start,
          end,
          filter: {
            job: {
              stage: isSpecificStage ? selectedJobType.jobStage.toLowerCase() : undefined,
              getJobsOfStage: !isSpecificStage ? getCurrentAndFutureStages(selectedJobType.jobStage, true) : undefined,
              getJobsOfState: selectedJobType.status ? [ selectedJobType.status ] : [ 'open', 'closed', 'archived' ],
              closedReason: has(selectedJobType, 'jobClosureReason') ? selectedJobType.jobClosureReason : undefined,
              excludeClosedReasons: excludeComplete ? [ 'complete' ] : [],
              attentionExplanations: has(selectedJobType, 'attentionExplanation') ? [ selectedJobType.attentionExplanation ] : undefined,
              noAttentionExplanation: selectedJobType?.attentionExplanation === 'None',
              ...engineJobFilters,
            },
            event: {
              ...engineEventFilters,
            },
            charge: {
              ...engineChargeFilter,
            }
          },
          ...engineJobsQueryVars,
        };
      default:
        throw new Error(`Not handled: ${report}`);
    }
  }

  /**
   * Returns an observable of the query parameters
   * needed to navigate to the report that supplies the data for a given dashboard item.
   */
  getReportQueryParams(report: DashboardReport) {
    return this.select(
      this.currency$,
      this.unixPeriod$,
      this.selectedJobType$,
      // We include zone even though we do not use it to avoid memoizing solely based on currency and month
      this.graphqlModule.zone,
      (currency, { start, end }, selectedJobType, zone) => {

        if(!zone) return null;

        return this.getQueryParams({
          start,
          end,
          currency,
          report,
          opts: {
            selectedJobType,
          }
        });
      },
    ).pipe(filter(Boolean));
  }

  getQueryParams(input: Parameters<typeof this.getQueryVars>[0]) {

    const {
      start,
      end,
      currency,
      report,
      opts,
    } = input;

    const queryVars = this.getQueryVars({
      start,
      end,
      currency,
      report,
      opts,
    });

    const formValue: { [ K in keyof ReportParamsForm['value'] ] : any }= {
      period: 'Custom',
      customPeriod: [
        new Date(start * 1000),
        new Date(end * 1000),
      ],
      periodType: queryVars.periodType,
      jobStage: queryVars.filter?.job?.getJobsOfStage?.length ?
        queryVars.filter?.job?.getJobsOfStage : queryVars.filter?.job?.stage ?
        [ queryVars.filter.job.stage ] : [],
      jobState: queryVars.filter?.job?.getJobsOfState?.length ? queryVars.filter?.job?.getJobsOfState : [ 'any' ],
      currency: queryVars.filter?.job?.currency,
      jobHasCompletedEventOfType: queryVars.filter?.job?.hasCompletedEventOfType,
      eventStatuses: queryVars.filter?.event?.getEventsOfStatus?.length ? queryVars.filter?.event?.getEventsOfStatus : [ 'any' ],
      eventTypes: queryVars.filter?.event?.getEventsOfType,
      groupBy: queryVars.groupBy,
      sort: queryVars.sort,
      dataColumns: queryVars.dataColumns as string[],
      aggregationColumns: queryVars.aggregationColumns as string[],
      excludeDamages: queryVars.filter?.charge?.excludeDamages,
      jobSources: queryVars.filter?.job?.sources?.length ? queryVars.filter.job.sources : [],
      noSource: queryVars.filter?.job?.noSource,
      noOrigin: queryVars.filter?.job?.noOrigin,
      jobOrigins: queryVars.filter?.job?.origins?.length ? queryVars.filter.job?.origins : [],
      jobEstimateMethods: queryVars.filter?.job?.estimateMethods?.length ? queryVars.filter.job.estimateMethods : [],
      jobEstimators: queryVars.filter?.job?.estimatorIds?.length ? queryVars.filter.job.estimatorIds : [],
      jobSalesAgents: queryVars.filter?.job?.salesAgentIds?.length ? queryVars.filter.job.salesAgentIds : [],
      attentionExplanations: queryVars.filter?.job?.attentionExplanations?.length ? queryVars.filter.job.attentionExplanations : [],
      noAttentionExplanation: queryVars.filter?.job?.noAttentionExplanation,
      closedReason: queryVars.filter?.job?.closedReason,
      utmSources: queryVars.filter?.job?.utmSources?.length ? queryVars.filter.job.utmSources : [],
      noUtmSource: queryVars.filter?.job?.noUtmSource,
      utmContents: queryVars.filter?.job?.utmContents?.length ? queryVars.filter.job.utmContents : [],
      noUtmContent: queryVars.filter?.job?.noUtmContents,
      utmMedia: queryVars.filter?.job?.utmMedia?.length ? queryVars.filter.job.utmMedia : [],
      noUtmMedium: queryVars.filter?.job?.noUtmMedium,
      utmTerms: queryVars.filter?.job?.utmTerms?.length ? queryVars.filter.job.utmTerms : [],
      noUtmTerm: queryVars.filter?.job?.noUtmTerm,
    }

    return this.queryParamsService.formatSearchFormValues(formValue, {
      dateRange: { controls: [ 'customPeriod' ]},
    });
  }

  getPercent(input: { total: number; converted: number }): number {
    return input.total ? ((input.converted / input.total) * 100) : 0;
  }

  calculateAverage(state: DashboardState, prefix: SizePrefix) {

    const countKey = `${prefix}Count` as const;

    const totalKey = `${prefix}Total` as const;

    const count = state[countKey];

    const total = state[totalKey];

    const average = count ? total / count : 0;

    const currency = state.currency === 'All' ? 'USD' : state.currency;

    return {
      count,
      total,
      average,
      currency,
    }
  }

  getDescription(input: {
    baseDescription: string;
    data: {
      total: number;
      count: number;
      currency: string;
    };
    extra?: string
  }) {

    const {
      baseDescription,
      data,
      extra,
    } = input;

    let output = baseDescription;

    const total = formatCurrency(data.total, data.currency);

    output += `the sum of their net subtotal (${total}) divided by their count (${data.count}). `

    output += extra || '';

    return output;
  }

  getCurrencyDelta(input: { current: CurrencyAmount; past: CurrencyAmount }): Omit<CurrencyDelta, 'loading'> {
    const amount = Math.abs(input.past.amount - input.current.amount);
    const sign = input.past.amount === input.current.amount ? 'neutral' :
      input.past.amount > input.current.amount ? 'negative' : 'positive';
    return {
      amount,
      sign,
      currency: input.current.currency,
    };
  }

  getAmountDelta(input: { current: number; past: number }): Omit<AmountDelta, 'loading'> {
    const amount = Math.abs(input.past - input.current);
    const sign = input.past === input.current ? 'neutral' :
      input.past > input.current ? 'negative' : 'positive';
    return {
      amount,
      sign,
    };
  }

  fetchActivity(input: {
    start: number;
    end: number;
    currency: string;
  }) {

    const { start, end, currency } = input;

    const transactionsPaidQueryVars = this.getQueryVars({ start, end, currency, report: 'transactionsPaid' });
    const eventsHappeningQueryVars = this.getQueryVars({ start, end, currency, report: 'eventsHappeningPerDay' });
    const eventsCreatedQueryVars = this.getQueryVars({ start, end, currency, report: 'eventsCreatedPerDay' });

    const customParams = {
      start: '$start',
      end: '$end',
      filter: '$filter'
    };

    const query = gql`
      query DashboardActivity(
        $start: Int!,
        $end: Int!,
        $filter: PreviewReportFilter,
      ) {
        transactionsPaid: previewReport(
          ${this.generatePreviewReportParams(transactionsPaidQueryVars, customParams)}
        ) {
          data
        }
        eventsHappening: previewReport(
          ${this.generatePreviewReportParams(eventsHappeningQueryVars, customParams)}
        ) {
          data
        }
        eventsCreated: previewReport(
          ${this.generatePreviewReportParams(eventsCreatedQueryVars, customParams)}
        ) {
          data
        }
      }
    `;

    return this.apollo.query<{
      transactionsPaid: { data?: any },
      eventsHappening: { data?: any },
      eventsCreated: { data?: any},
    }>({
      query,
      variables: {
        start,
        end,
        currency,
        filter: eventsCreatedQueryVars.filter,
      },
    });
  }

  fetchEngineJobs(input: {
    start: number;
    end: number;
    jobTypes: {
      jobType: SelectedJobType,
      key: string,
      metrics: {
        jobCount: number,
        revenue: number,
      }
    }[],
    currency: string;
    sortBy: SortBy;
    pipelinePeriod: PipelinePeriod;
    source?: string;
    jobOrigin?: string;
    estimateMethod?: string;
    estimator?: string;
    salesAgent?: string;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmContent?: string;
    utmTerm?: string;
    skip?: number;
    jobUpdated?: string;
  }) {

    const {
      start,
      end,
      jobTypes,
      currency,
      sortBy,
      skip,
      source,
      jobOrigin,
      estimateMethod,
      estimator,
      salesAgent,
      utmSource,
      utmMedium,
      utmCampaign,
      utmContent,
      utmTerm,
      jobUpdated,
      pipelinePeriod,
    } = input;

    const queryFilters: string[] = [];
    const queries: string[] = [];
    const filterVariables: Record<string, PreviewReportFilter> = {};

    for (const jobType of jobTypes) {

      const key = encodeKeyForGraphQL(jobType.key);

      const selectedJobType = jobType.jobType;

      const filterKey = `${key}_filter`;

      const filterName = `$${filterKey}`;

      const filter = `${filterName}: PreviewReportFilter`;

      queryFilters.push(filter);

      const queryVars = this.getQueryVars({
        start,
        end,
        currency,
        report: 'engineJobs',
        opts: {
          selectedJobType: {
            ...selectedJobType,
            source,
            jobOrigin,
            estimateMethod,
            estimator,
            salesAgent,
            utmSource,
            utmMedium,
            utmCampaign,
            utmContent,
            utmTerm,
          },
          pipelinePeriod,
          sortBy,
          skip,
        },
      });

      filterVariables[filterKey] = queryVars.filter;

      const query = `
        ${key}: previewReport(
          ${this.generatePreviewReportParams(queryVars, { start: '$start', end: '$end', skip: '$skip', filter: filterName })}
        ) {
          data
        }
      `;

      queries.push(query);
    }

    const query = gql`
      query EngineJobs(
        $start: Int!,
        $end: Int!,
        $skip: Int,
        ${queryFilters.join(',\n')}
      ) {
        ${queries.join('\n')}
      }
    `;

    return this.apollo.query<DashboardState['engineJobs']>({
      query,
      variables: {
        start,
        end,
        skip,
        ...filterVariables,
      },
      fetchPolicy: jobUpdated ? 'network-only' : 'cache-first',
    });
  }

  generatePreviewReportParams(
    input: PreviewReportQueryVariables,
    customParams?: Partial<Record<keyof PreviewReportQueryVariables, string>>,
  ) {

    const params: (keyof PreviewReportQueryVariables)[] = [
      'start',
      'end',
      'skip',
      'filter',
      'groupBy',
      'periodType',
      'dataColumns',
      'aggregationColumns',
      'sort',
      'performance',
      'linkFormat',
      'limit',
    ];

    const lines = [];

    for (const param of params) {

      if (customParams[param]) {
        lines.push(`${param}: ${customParams[param]}`);
        continue;
      }

      const value = input[param];

      if (!value) { continue; }

      const isArray = Array.isArray(value);

      if (isArray) {
        lines.push(`${param}: [ ${value.map((el) => `"${el}"`).join(',')} ]`);
        continue;
      }

      if (typeof value === 'number') {
        lines.push(`${param}: ${value}`);
        continue;
      }

      // By this point, we'll assume the value is of string type
      lines.push(`${param}: "${value}"`);
    }

    return lines.join(`, `)
  }

  /**
   * Formats event currency and adds a total row to the end of the data.
   */
  formatEventData(data: ReportData, selectedCurrency: string) {

    const { headers, rows } = data;

    const currencyIndex = headers.indexOf('Currency');

    const formattedHeaders = headers.slice(0, currencyIndex);

    if (!data.rows.length) {
      return {
        headers: formattedHeaders,
        rows: [],
      }
    }

    let currency: string;

    if (selectedCurrency === 'All') {

      for (const row of rows) {

        if (!currency) {
          currency = row[currencyIndex];
        }

        if (currency === baseEnvironment.defaultCurrency) {
          break;
        }
      }
    } else {
      currency = selectedCurrency;
    }

    let subTotalSum = 0;
    let eventCountSum = 0;

    const eventTypeMap: Record<string, {
      netSubTotal: number;
      eventCount: number;
      average: number;
    }> = {};

    for (const [ eventType, netSubTotalRaw, eventCountRaw ] of rows) {

      const currentValue = eventTypeMap[eventType];

      const defaultValue = {
        netSubTotal: 0,
        eventCount: 0,
        average: 0,
      };

      const value = currentValue || defaultValue;

      const eventCount = parseInt(eventCountRaw);
      const netSubTotal = parseFloat(netSubTotalRaw);

      value.eventCount += eventCount;
      value.netSubTotal += netSubTotal;
      value.average = value.eventCount ? value.netSubTotal / value.eventCount : 0;

      subTotalSum += netSubTotal;
      eventCountSum += eventCount;

      eventTypeMap[eventType] = value;
    }

    const average = eventCountSum ? subTotalSum / eventCountSum : 0;

    const formattedRows: string[][] = Object.entries(eventTypeMap)
      .map(([ eventType, { netSubTotal, eventCount, average }]) => ([
        eventType,
        formatCurrency(netSubTotal, currency),
        eventCount.toString(),
        formatCurrency(average, currency),
      ]));

    formattedRows.push([
      'Total',
      formatCurrency(subTotalSum, currency),
      eventCountSum.toString(),
      formatCurrency(average, currency),
    ]);

    return {
      headers: formattedHeaders,
      rows: formattedRows
    }
  }

  notifyBottomReached(path: DashboardPath) {
    this.bottomReached$.next(path);
  }

  notifyJobUpdated(jobId: string) {
    this.jobUpdated$.next(jobId);
  }

  get routeActivated$() {
    return this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.activatedRoute.firstChild.snapshot.routeConfig.path as DashboardPath),
      startWith(this.activatedRoute.firstChild?.snapshot?.routeConfig?.path as DashboardPath),
    );
  }

  getEngineJobsHasMore(
    newJobs: DashboardState['engineJobs'],
    jobTypes: ExtractObservableType<typeof this.engineJobsInfo$>['jobTypes'],
  ) {

    if (jobTypes.length !== 1) { return false; }

    const [ { key } ] = jobTypes;

    const graphQLKey = encodeKeyForGraphQL(key);

    const newRows = newJobs[graphQLKey]?.data?.rows || [];

    return !(newRows.length < ENGINE_JOBS_QUERY_LIMIT);
  }

}

function getCountAndTotal(serverResponse: ApolloQueryResult<AggregationDataOnlyQuery>): { count: number; total: number; } {

  let total = 0;
  let count = 0;

  for (const [ rowAmount, rowCount ] of serverResponse.data.previewReport.aggregationData.rows) {
    total += parseFloat(rowAmount);
    count += parseInt(rowCount);
  }

  return { total, count };
}

function getRevenueAndLogisticsTotals(
  res: ApolloQueryResult<DataOnlyQuery>,
  selectedCurrency: string,
) {

  const { rows } = res.data.previewReport.data;

  if (!rows.length) {
    return {
      realizedRevenue: getDefaultAmount(),
      scheduledRevenue: getDefaultAmount(),
      expectedRevenue: getDefaultAmount(),
      sharedRevenue: getDefaultAmount(),
      distanceBooked: 0,
      distanceCompleted: 0,
      travelTimeBooked: 0,
      travelTimeCompleted: 0,
    }
  }

  let currency: string;

  if (selectedCurrency === 'All') {
      for (const row of rows) {
        if (!currency) {
          currency = row[8];
        } else if (currency === baseEnvironment.defaultCurrency) {
          break;
        }
      }
  } else {
    currency = selectedCurrency;
  }

  if (!currency) {
    currency = baseEnvironment.defaultCurrency;
  }

  let realizedRevenueTotal = 0;
  let scheduledRevenueTotal = 0;
  let expectedRevenueTotal = 0;
  let sharedRevenueTotal = 0;
  let distanceBookedTotal = 0;
  let distanceCompletedTotal = 0;
  let travelTimeBookedTotal = 0;
  let travelTimeCompletedTotal = 0;

  for (const [
    realizedRevenue,
    scheduledRevenue,
    expectedRevenue,
    sharedRevenue,
    distanceBooked,
    distanceCompleted,
    travelTimeBooked,
    travelTimeCompleted,
  ] of rows) {
    realizedRevenueTotal += parseFloat(realizedRevenue);
    scheduledRevenueTotal += parseFloat(scheduledRevenue);
    expectedRevenueTotal += parseFloat(expectedRevenue);
    sharedRevenueTotal += parseFloat(sharedRevenue);
    distanceBookedTotal += parseFloat(distanceBooked);
    distanceCompletedTotal += parseFloat(distanceCompleted);
    travelTimeBookedTotal += parseFloat(travelTimeBooked);
    travelTimeCompletedTotal += parseFloat(travelTimeCompleted);
  }

  return {
    realizedRevenue: {
      amount: realizedRevenueTotal,
      currency,
    },
    scheduledRevenue: {
      amount: scheduledRevenueTotal,
      currency,
    },
    expectedRevenue: {
      amount: expectedRevenueTotal,
      currency,
    },
    sharedRevenue: {
      amount: sharedRevenueTotal,
      currency,
    },
    distanceBooked: distanceBookedTotal,
    distanceCompleted: distanceCompletedTotal,
    travelTimeBooked: travelTimeBookedTotal,
    travelTimeCompleted: travelTimeCompletedTotal,
  };
}

function getLabelsFromCount(
  metricsBreakdown: Record<string, { jobCount: number; revenue: number }>,
  selectedMetric: PipelineMetric,
  currency: string,
  opts?: {
    pluralizeLabel?: boolean,
    isName?: boolean,
  }
) {

  const {
    pluralizeLabel,
    isName,
  } = opts || {};

  if (currency === 'All') {
    currency = 'USD';
  }

  return Object.fromEntries(Object.entries(metricsBreakdown)
    .map(( [ key, metrics ]) => {

      const amount = selectedMetric === 'Job Count' ? metrics.jobCount : metrics.revenue;

      const formattedAmount = selectedMetric === 'Job Count' ? amount : formatCurrency(amount, currency);

      let label = key;

      if (pluralizeLabel) {
        label = pluralize(label);
      }

      if (isName && label !== 'None') {
        label = decodeUser(label).name;
      }

      return ( [ key, `${ label } (${ formattedAmount })` ]);
    }));;
}


function isSelected(flowJobType: SelectedJobType, selectedJobType: SelectedJobType) {

  const isStageMatch = selectedJobType.jobStage === flowJobType.jobStage;

  if (isNil(selectedJobType.status)) {
    return isStageMatch;
  }

  const isStatusMatch = selectedJobType.status === flowJobType.status;

  // Special case
  if (selectedJobType.jobStage === 'Invoice' && selectedJobType.status === 'closed' && isNil(selectedJobType.jobClosureReason)) {
    return isStageMatch && isStatusMatch && (!flowJobType.jobClosureReason || flowJobType.jobClosureReason !== 'complete');
  }

  if (isNil(selectedJobType.jobClosureReason) && isNil(selectedJobType.attentionExplanation)) {
    return isStageMatch && isStatusMatch;
  }

  if (selectedJobType.status === 'closed') {
    return isStageMatch && isStatusMatch
     && selectedJobType.jobClosureReason === flowJobType.jobClosureReason;
  }

  return isStageMatch && isStatusMatch
    && selectedJobType.attentionExplanation === flowJobType.attentionExplanation;
}

function getCurrentAndFutureStages(jobStage?: JobStage, toLower?: boolean) {
  if (!jobStage) { return [] };

  const stageIndex = jobStages.indexOf(jobStage);

  const currentAndFutureStages = jobStages
    .slice(stageIndex);

  if (!toLower) {
    return currentAndFutureStages;
  }

  return currentAndFutureStages.map((s) => s.toLowerCase());
}


export function getSelectedJobData(row: string[], columnKey: typeof selectedJobsColumns[number]) {
  const columnIndex = selectedJobsColumns.indexOf(columnKey);
  return row[columnIndex];
}

export function getCurrentMonth() {
  return dayjs().format('MMMM YYYY');
}

export function getCurrentMonthPeriod() {
  const today = dayjs();
  return [ today.startOf('month').toDate(), today.endOf('month').toDate() ];
}

export function mergeInDefaultFilters(
  currency: string,
  additionalFilters?: PreviewReportQueryVariables['filter'],
): PreviewReportQueryVariables['filter'] {
  const defaultFilters = {
    job: {
      getJobsOfState: [ 'open', 'closed', 'archived' ],
      currency: currency === 'All' ? undefined : currency,
    },
    event: {
      getEventsOfStatus: [ 'required', 'pending', 'booked', 'confirmed', 'completed' ],
    },
  }

  if (!additionalFilters) {
    return defaultFilters;
  }

  // Do not merge arrays, just replace them
  const customizer = (objVal, srcVal) => {
    if (Array.isArray(objVal)) {
      return srcVal;
    }
  }

  // mergeWith mutates its first argument, clone to avoid bugs
  const clone = cloneDeep(defaultFilters);

  return mergeWith(clone, additionalFilters, customizer);
}

function extractName(userString: string) {
  const endOfNameIndex = userString.indexOf(' <');
  return userString.slice(0, endOfNameIndex);
}

function extractId(userString: string) {
  const startOfIdIndex = userString.lastIndexOf('/') + 1;
  const endOfIdIndex = userString.indexOf('?');
  return userString.slice(startOfIdIndex, endOfIdIndex);
}

function parseJobs(rows: string[][]): SelectedJob[] {

  const output = [];

  for (const row of rows) {

    const rawValues = selectedJobsColumns
    .reduce((acc, col) => {

      const value = row[selectedJobsColumns.indexOf(col)];

      if (value === 'None') {
        return acc;
      }

      return {
        ...acc,
        [col]: value,
      }
    }, {}) as Record<
    typeof selectedJobsColumns[number],
    string
    >;

    const parsedJob: SelectedJob = {
      code: extractName(rawValues.job),
      id: extractId(rawValues.job),
      icon: jobStageIcons[rawValues.jobStage.toLowerCase()],
      discountedSubTotal: rawValues.discountedSubTotal,
      createdAt: rawValues.jobCreatedAt,
      description: `${rawValues.jobStatus} ${rawValues.jobStage}`,
      customer: {
        name: extractName(rawValues.customer),
        id: extractId(rawValues.customer),
      },
      status: rawValues.jobStatus as 'Open' | 'Closed',
    };

    if (rawValues.estimator) {
      parsedJob.estimator = {
        name: extractName(rawValues.estimator),
        id: extractId(rawValues.estimator),
      }
    }

    output.push(parsedJob);
  }

  return output;
}

function calculateLabelCount(
  jobInfoCount: Record<string, {
    jobCount: number;
    revenue: number;
  }>,
  expandedFlows: ExpandedFlow[],
  attentionExplanations: DashboardState['attentionExplanations'],
  jobClosureReasons: DashboardState['jobClosureReasons']
) {

  // Top to bottom
  const statusPriority: Record<string, number> = {};

  // Left to right
  const statusColumn: Record<string, number> = {};

  const labelCount: Record<string, { jobCount: number; revenue: number; jobType: SelectedJobType }> = {};

  const jobInfoKeys = Object.keys(jobInfoCount)
    .map((key) => key.split(delimiter))
    .filter(([ stage, status, reason ]) => {

      const jobStage = stage as JobStage;

      const jobStatus = status as 'open' | 'closed' | undefined;

      const isClosedInvoice = jobStage === 'Invoice' && jobStatus === 'closed';

      // Should always be included
      if (isClosedInvoice && reason === 'complete') {
        return true;
      }

      // Should never be included
      if (isClosedInvoice && !reason) {
        return false;
      }

      const doExpand = expandedFlows
        .some((flow) => flow.jobStage === jobStage && status === flow.status);

      if (isClosedInvoice && reason === 'not-complete') {
        return !doExpand;
      }

      return doExpand === Boolean(reason);
    })
    .map((jobInfo) => jobInfo.join(delimiter));

  for (const serializedJobInfo of jobInfoKeys) {

    const [ stage, jobStatus, reason ] = serializedJobInfo.split(delimiter);

    const jobStage = stage as JobStage;

    const status = jobStatus as 'open' | 'closed';

    const isClosed = status === 'closed';

    const isInvoice = jobStage === 'Invoice';

    const isFinalStage = isInvoice && isClosed && reason === 'complete';

    const addDetails = expandedFlows
      .some((flow) => flow.jobStage === jobStage && (status === flow.status))
      || isFinalStage;

    const jobClosureReason = isClosed ? reason : null;

    const attentionExplanation = !isClosed ? reason : null;

    const jobType = {
      jobStage,
      status: status,
      attentionExplanation: !isClosed && addDetails ? attentionExplanation : null,
      jobClosureReason: isClosed && addDetails ? jobClosureReason : null,
    };

    const label = getTitle(jobType, attentionExplanations, jobClosureReasons);

    // // Completed invoices in the middle, closed jobs at the bottom, open jobs at the top
    statusPriority[label] = isFinalStage ? 1 : isClosed ? 2 : 0;
    statusColumn[label] = stageColumn[jobStage] + 1;

    labelCount[label] = labelCount[label] || {
      jobCount: 0,
      revenue: 0,
      jobType,
    }

    const { jobCount, revenue } = jobInfoCount[serializedJobInfo];

    labelCount[label].jobCount += jobCount;
    labelCount[label].revenue += revenue;
  }

  return { labelCount, statusColumn, statusPriority };
}

function getJobClosureReasons(configs: BaseConfigValueFragment[]) {
  const closureReasons: {
    id: string;
    title: string;
    stages: string[];
  }[] = getParsedConfigValueByKey(configs, 'jobs.closedReasons', []);

  return closureReasons.reduce((acc, curr) => ({ ...acc, [curr.id]: curr.title }), {});
}

function getAttentionExplanations(configs: BaseConfigValueFragment[]) {
  const attentionExplanations: {
    attentionExplanation: string;
    label: string;
    description: string;
  }[] = configs
    .filter((c) => c.key.startsWith('attention'))
    .map((c) => JSON.parse(c.value));

  return attentionExplanations.reduce((acc, curr) => ({
    ...acc,
    [curr.attentionExplanation]: { label: curr.label, description: curr.description },
  }), {});
}

function getTitle(
  selectedJobType: SelectedJobType,
  attentionExplanations: DashboardState['attentionExplanations'],
  closureReasons: DashboardState['jobClosureReasons'],
  includeStageAndStatus = true,
  defaultTitle = 'No Jobs Selected'
) {

  if (selectedJobType === null) {
    return defaultTitle;
  }

  const {
    jobStage,
    status,
    jobClosureReason,
    attentionExplanation,
  } = selectedJobType;

  let title = `${pluralize(jobStage)}`;

  if (!status) {
    return title;
  }

  title = `${strToTitleCase(status)} ${title}`;

  if (jobClosureReason) {

    const formattedClosureReason = closureReasons[jobClosureReason] || startCase(jobClosureReason);

    title = includeStageAndStatus ? `${title}: ${ formattedClosureReason }` : formattedClosureReason;
  }

  if (attentionExplanation && attentionExplanation === 'None') {

    const noneTitle = 'No Action Required';

    title = includeStageAndStatus ? `${title} (${noneTitle})` : noneTitle;

  } else if (attentionExplanation) {

    const formattedAttentionExplanation = attentionExplanations[attentionExplanation] ?
      attentionExplanations[attentionExplanation].label :
      startCase(attentionExplanation);

    title = includeStageAndStatus ? `${title}: ${formattedAttentionExplanation}` : formattedAttentionExplanation;
  }

  return title;
}

function getMaxStackedFlows(dataPoints: SankeyDataPoint[]) {

  const flowCount = {};

  for (const dataPoint of dataPoints) {
    const fromKey = [ 'from', dataPoint.from ].join(':');
    flowCount[fromKey] = flowCount[fromKey] || 0;
    flowCount[fromKey] += 1;

    const toKey = [ 'to', dataPoint.to ].join(':');
    flowCount[toKey] = flowCount[toKey] || 0;
    flowCount[toKey] += 1;
  }

  return Math.max(...Object.values(flowCount) as number[]);
}

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i< str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

/**
 * Encodes a key by replacing all hyphens '-' with three underscores '___'
 * for use in GraphQL queries as aliases or field names.
 *
 * @param key The key to be encoded.
 * @returns The encoded key.
 */
function encodeKeyForGraphQL(key: string): string {
  return key.replace(/-| /g, '___');
}

function mergeJobs(input: {
  currentJobs: DashboardState['engineJobs'];
  newJobs: DashboardState['engineJobs'];
  graphQLKey: string;
}) {

  const { currentJobs, newJobs, graphQLKey } = input;

  const newRows = newJobs[graphQLKey]?.data?.rows;

  // Nothing to merge
  if (!newRows.length) {
    return currentJobs;
  }

  const currentRows = currentJobs[graphQLKey]?.data?.rows || [];

  const updatedJobs = cloneDeep(currentJobs);

  updatedJobs[graphQLKey] = {
    data: {
      rows: [ ...currentRows, ...newRows ],
    }
  }

  return updatedJobs;
}

function getColor(
  flowJobType: SelectedJobType,
  selectedJobType: SelectedJobType,
  style: CSSStyleDeclaration,
  highlightAll?: boolean,
) {

  const defaultColor = '#cc6fbc';

  if (!flowJobType) {
    return defaultColor;
  }

  const categoryKeys: (keyof SelectedJobType)[] = [
    'source',
    'jobOrigin',
    'estimateMethod',
    'estimator',
    'salesAgent',
    'utmSource',
    'utmTerm',
    'utmMedium',
    'utmCampaign',
    'utmContent',
  ];

  for (const key of categoryKeys) {
    if (!flowJobType[key]) { continue; }

    if ([ 'estimator', 'salesAgent' ].includes(key)) {
      return style.getPropertyValue(getColorByCategory(getUserName(flowJobType[key as ('salesAgent' | 'estimator')])));
    }

    return style.getPropertyValue(getColorByCategory(flowJobType[key] as string));
  }

  const { jobStage } = flowJobType;

  const stageColors = {
    'Lead': '--cyan-200',
    'Estimate': '--yellow-500',
    'Booking': '--purple-600',
    'Invoice': '--success',
  };

  const stagecolor = style.getPropertyValue(stageColors[jobStage]);

  if (highlightAll || isSelected(flowJobType, selectedJobType)) {
    return getHoverColor(stagecolor);
  }

  return stagecolor;
}

function getColorByCategory(categoryName: string): string {

  const colorPaletteArray = [
    '--red-light',
    '--maroon-light',
    '--orange-light',
    '--pink-light',
    '--beige-light',
    '--green-light',
    '--teal-light',
    '--cyan-light',
    '--navy-light',
    '--blue-light',
    '--magenta-light',
    '--blue-600',
    '--green-600',
    '--yellow-600',
    '--pink-600',
    '--indigo-600',
    '--teal-600',
    '--orange-500',
  ];

  const index = hashString(categoryName) % colorPaletteArray.length;
  return colorPaletteArray[index];
}

function getPeriodTypeForPipelinePeriod(pipelinePeriod: PipelinePeriod) {

  const periodMap: Record<PipelinePeriod, string> = {
      'Lead Created': 'jobCreatedAt',
      Estimated: 'jobEstimateStageDate',
      Booked: 'jobBookingStageDate',
      Happening: 'eventStart',
      Invoiced: 'jobInvoiceStageDate',
      Completed: 'jobClosedAt',
  };

  return periodMap[pipelinePeriod];
}

function getEventTypeFilter(pipelinePeriod: PipelinePeriod): PreviewReportFilter['event']['getEventsOfType'] {

  if (pipelinePeriod === 'Happening') {
    return [ 'moving', 'delivery', 'packing', 'unpacking' ];
  }

  return [];
}

function getUserName(raw: string) {

  if (raw === 'None') { return raw; }

  return decodeUser(raw).name;
}

export function decodeUser(raw: string): User {

  if (raw === 'None') { return; }

  const [ givenName, familyName, id ] = raw.split('.');

  return {
    name: `${givenName} ${familyName}`,
    id,
  }
}

function has<T extends object, K extends keyof T>(
  value: T,
  key: K,
  invalid: 'None' | 'Not specified' = 'None',
) {
  if (!value[key]) { return false; }
  return value[key] !== invalid;
}
